import type {
  SyncResponse,
  MeResponse,
  OrganizationListResponse,
  OrganizationResponse,
  CreateOrganizationInput,
  UpdateOrganizationInput,
  SchoolTypeListResponse,
  SchoolListResponse,
  SchoolResponse,
  CreateSchoolInput,
  UpdateSchoolInput,
  PlayerListResponse,
  PlayerResponse,
  PlayerImportResponse,
  CreatePlayerInput,
  UpdatePlayerInput,
  CourtListResponse,
  CourtResponse,
  CreateCourtInput,
  UpdateCourtInput,
  AssetTypePublic,
  AssetPublic,
  AdSpacePublic,
  PlacementPublic,
  OfflineScoreQueueItem,
  OfflineScoreSport,
  OfflineScoreOp,
} from "@dugads/shared";

import { logger } from "./logger";
import { getFirebaseAuth } from "./firebase";
import {
  enqueueScoreWrite,
  drainScoreQueue,
  type DrainResult,
} from "./offline/write-queue";

// Per ADR-006: all API calls use Authorization: Bearer <Firebase ID token>

const API_BASE = "/api";

// Shared query params for server-side list endpoints (search/sort/pagination).
// Mirrors the listQuerySchema contract in @dugads/shared.
export interface ListParams {
  page?: number;
  per_page?: number;
  search?: string;
  sort?: string;
  order?: "asc" | "desc";
}

/** Serialise ListParams (plus any extra params) into a query string, omitting empties. */
export function listParamsToQuery(
  params?: ListParams,
  extra?: Record<string, string | undefined>,
): string {
  const p = new URLSearchParams();
  if (params?.page && params.page > 1) p.set("page", String(params.page));
  if (params?.per_page) p.set("per_page", String(params.per_page));
  if (params?.search) p.set("search", params.search);
  if (params?.sort) p.set("sort", params.sort);
  if (params?.order) p.set("order", params.order);
  for (const [k, v] of Object.entries(extra ?? {})) {
    if (v) p.set(k, v);
  }
  const qs = p.toString();
  return qs ? `?${qs}` : "";
}

// Impersonation header -- set by ImpersonationContext, injected into every request.
let _impersonationUserId: string | null = null;
export function setImpersonationUserId(id: string | null) { _impersonationUserId = id; }
export function getImpersonationUserId(): string | null { return _impersonationUserId; }

/** Get current Firebase ID token, or null if not signed in */
async function getIdToken(): Promise<string | null> {
  const auth = getFirebaseAuth();
  if (!auth?.currentUser) return null;
  try {
    return await auth.currentUser.getIdToken();
  } catch {
    return null;
  }
}

async function request<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const method = options?.method ?? "GET";
  const token = await getIdToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options?.headers as Record<string, string> | undefined),
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (_impersonationUserId) {
    headers["X-Impersonate-UserId"] = _impersonationUserId;
  }
  // Offline queue replays pass a fixed X-Request-Id (the client_event_id) so a
  // future server idempotency layer can dedupe; otherwise generate a fresh one.
  headers["X-Request-Id"] = headers["X-Request-Id"] ?? crypto.randomUUID();

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message = body.error ?? "Request failed";
    if (res.status === 401) {
      logger.warn({ method, path }, "Unauthorized");
    } else if (res.status >= 500) {
      logger.error({ method, path, status: res.status, message }, "API error");
    } else {
      logger.debug({ method, path, status: res.status, message }, "API client error");
    }
    throw new ApiError(res.status, message, body);
  }

  // 204 No Content -- return undefined cast; callers use <void>.
  if (res.status === 204) return undefined as T;

  return res.json();
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// --- Offline Support (120): score write-queue plumbing ---

/**
 * Thrown by a queueable score-write helper when the request failed before the
 * server responded (offline / timeout) and was instead saved to the offline
 * queue. Score pages catch this and show a "queued" state rather than an error.
 */
export class QueuedOfflineError extends Error {
  constructor(public item: OfflineScoreQueueItem) {
    super("Saved offline -- will sync when you reconnect");
    this.name = "QueuedOfflineError";
  }
}

interface QueueMeta {
  sport: OfflineScoreSport;
  op: OfflineScoreOp;
  matchId: string;
  label: string;
  method: "POST" | "DELETE" | "PATCH";
  path: string;
  body: unknown | null;
}

/**
 * Run a score write; if it fails because the server was unreachable (a non-
 * ApiError -- fetch rejects with a TypeError when offline), persist it to the
 * offline queue and throw QueuedOfflineError. An ApiError means the server
 * responded and refused, so it is a real error and re-thrown unchanged.
 */
async function queueableWrite<T>(meta: QueueMeta, exec: () => Promise<T>): Promise<T> {
  try {
    return await exec();
  } catch (err) {
    if (err instanceof ApiError) throw err; // server responded -- genuine error
    const item: OfflineScoreQueueItem = {
      client_event_id: crypto.randomUUID(),
      sport: meta.sport,
      op: meta.op,
      match_id: meta.matchId,
      method: meta.method,
      path: meta.path,
      body: meta.body,
      label: meta.label,
      status: "pending",
      queued_at: Date.now(),
      attempts: 0,
      rejection: null,
    };
    await enqueueScoreWrite(item);
    throw new QueuedOfflineError(item);
  }
}

/**
 * Replay all queued score writes against the server (call on reconnect). The
 * queue owns ordering + rejection handling; api.ts supplies the actual HTTP
 * replay using the item's client_event_id as the idempotency-anchored
 * X-Request-Id.
 */
export function drainScoreQueueOnline(): Promise<DrainResult> {
  return drainScoreQueue(async (item) => {
    await request(item.path, {
      method: item.method,
      headers: { "X-Request-Id": item.client_event_id },
      ...(item.body != null ? { body: JSON.stringify(item.body) } : {}),
    });
  });
}

// Per ADR-006: sync replaces register/login. Firebase SDK handles auth.
export function sync(data?: { first_name?: string; last_name?: string }) {
  return request<SyncResponse>("/auth/sync", {
    method: "POST",
    body: JSON.stringify(data ?? {}),
  });
}

export function getMe() {
  return request<MeResponse>("/auth/me");
}

// Organizations

export function getOrganizations(params?: ListParams) {
  return request<OrganizationListResponse & { total?: number }>(
    `/organizations${listParamsToQuery(params)}`,
  );
}

export function getOrganization(id: string) {
  return request<OrganizationResponse>(`/organizations/${id}`);
}

export function createOrganization(data: CreateOrganizationInput) {
  return request<OrganizationResponse>("/organizations", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateOrganization(id: string, data: UpdateOrganizationInput) {
  return request<OrganizationResponse>(`/organizations/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteOrganization(id: string) {
  return request<void>(`/organizations/${id}`, { method: "DELETE" });
}

// School Types

export function getSchoolTypes() {
  return request<SchoolTypeListResponse>("/school-types");
}

// Schools

export function getSchools(organizationId?: string) {
  const params = organizationId ? `?organization_id=${organizationId}` : "";
  return request<SchoolListResponse>(`/schools${params}`);
}

export function getSchool(id: string) {
  return request<SchoolResponse>(`/schools/${id}`);
}

export function createSchool(data: CreateSchoolInput) {
  return request<SchoolResponse>("/schools", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateSchool(id: string, data: UpdateSchoolInput) {
  return request<SchoolResponse>(`/schools/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteSchool(id: string) {
  return request<{ success: boolean }>(`/schools/${id}`, {
    method: "DELETE",
  });
}

// Players

export interface PlayerListParams extends ListParams {
  active?: boolean;
  eligible_for?: string;
  /** CSV of sport_ids */
  sport?: string;
  /** CSV of age_group_ids (server resolves to DOB ranges) */
  age_group?: string;
  indemnity?: "complete" | "incomplete";
}

export function getPlayers(schoolId: string, opts?: PlayerListParams) {
  const qs = listParamsToQuery(opts, {
    active: opts?.active === undefined ? undefined : String(opts.active),
    eligible_for: opts?.eligible_for,
    sport: opts?.sport,
    age_group: opts?.age_group,
    indemnity: opts?.indemnity,
  });
  return request<PlayerListResponse & { total?: number }>(`/schools/${schoolId}/players${qs}`);
}

export function getAllPlayers(params?: ListParams) {
  return request<PlayerListResponse & { total: number }>(`/players${listParamsToQuery(params)}`);
}

export function getEligiblePlayers(teamId: string) {
  return request<PlayerListResponse>(`/teams/${teamId}/eligible-players`);
}

export function advanceGrades(schoolId: string) {
  return request<{ updated: number }>(`/schools/${schoolId}/players/advance-grade`, {
    method: "PUT",
  });
}

export function getPlayer(id: string) {
  return request<PlayerResponse>(`/players/${id}`);
}

export function createPlayer(schoolId: string, data: CreatePlayerInput) {
  return request<PlayerResponse>(`/schools/${schoolId}/players`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updatePlayer(id: string, data: UpdatePlayerInput) {
  return request<PlayerResponse>(`/players/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// Player enrollment endpoints

export function addPlayerEnrollment(playerId: string, data: { sport_id: string; indemnity_complete?: boolean; also_eligible_for?: string[]; club_id?: string | null }) {
  return request<{ enrollment: import("@dugads/shared").PlayerEnrollmentPublic }>(`/players/${playerId}/enrollments`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// 155 -- blank indemnity form template URL
export function getIndemnityBlankForm() {
  return request<{ url: string }>(`/indemnity/blank-form`);
}

export function updatePlayerEnrollment(playerId: string, sportId: string, data: { indemnity_complete?: boolean; indemnity_document_url?: string | null; also_eligible_for?: string[]; club_id?: string | null; age_group_override_id?: string | null; age_group_override_reason?: string | null }) {
  return request<{ enrollment: import("@dugads/shared").PlayerEnrollmentPublic }>(`/players/${playerId}/enrollments/${sportId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deletePlayerEnrollment(playerId: string, sportId: string) {
  return request<{ success: boolean }>(`/players/${playerId}/enrollments/${sportId}`, {
    method: "DELETE",
  });
}

export function togglePlayerLogin(playerId: string, enabled: boolean) {
  return request<{ enabled: boolean }>(`/players/${playerId}/toggle-login`, {
    method: "POST",
    body: JSON.stringify({ enabled }),
  });
}

export function deletePlayer(id: string) {
  return request<{ success: boolean }>(`/players/${id}`, {
    method: "DELETE",
  });
}

export async function importPlayers(schoolId: string, file: File) {
  const formData = new FormData();
  formData.append("file", file);

  const token = await getIdToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}/schools/${schoolId}/players/import`, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.error ?? "Import failed");
  }

  return res.json() as Promise<PlayerImportResponse>;
}

export async function exportFamilyCodes(schoolId: string) {
  const token = await getIdToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}/schools/${schoolId}/players/export-codes`, {
    headers,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.error ?? "Export failed");
  }

  return res.blob();
}

// Courts

export function getCourts(schoolId: string, params?: ListParams) {
  return request<CourtListResponse & { total?: number }>(`/schools/${schoolId}/courts${listParamsToQuery(params)}`);
}

export function getAllCourts(params?: ListParams & { court_type_id?: string; school_id?: string }) {
  const qs = listParamsToQuery(params, {
    court_type_id: params?.court_type_id,
    school_id: params?.school_id,
  });
  return request<CourtListResponse & { total: number }>(`/courts${qs}`);
}

export function getCourt(id: string) {
  return request<CourtResponse>(`/courts/${id}`);
}

export function createCourt(schoolId: string, data: CreateCourtInput) {
  return request<CourtResponse>(`/schools/${schoolId}/courts`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateCourt(id: string, data: UpdateCourtInput) {
  return request<CourtResponse>(`/courts/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteCourt(id: string) {
  return request<{ success: boolean }>(`/courts/${id}`, {
    method: "DELETE",
  });
}

// Court Types

export function getCourtTypes() {
  return request<import("@dugads/shared").CourtTypeListResponse>("/court-types");
}

// Sports

export function getSports() {
  return request<import("@dugads/shared").SportListResponse>("/sports");
}

/** Super-admin: list ALL sports including system-disabled ones (catalogue management). */
export function getAllSports() {
  return request<import("@dugads/shared").SportListResponse>("/sports?all=true");
}

/** Super-admin: enable/disable a sport system-wide. */
export function setSportEnabled(sportId: string, enabled: boolean) {
  return request<import("@dugads/shared").SportResponse>(`/sports/${sportId}`, {
    method: "PATCH",
    body: JSON.stringify({ enabled }),
  });
}

export function getSchoolSports(schoolId: string) {
  return request<import("@dugads/shared").SchoolSportsResponse>(`/schools/${schoolId}/sports`);
}

export function enableSchoolSport(schoolId: string, sportId: string) {
  return request<{ message: string }>(`/schools/${schoolId}/sports/${sportId}`, {
    method: "PUT",
  });
}

export function disableSchoolSport(schoolId: string, sportId: string) {
  return request<{ message: string }>(`/schools/${schoolId}/sports/${sportId}`, {
    method: "DELETE",
  });
}

export function getCourtLabel() {
  return request<import("@dugads/shared").CourtLabelResponse>("/me/court-label");
}

// Asset Types

export function getAssetTypes(params?: ListParams) {
  return request<{ asset_types: AssetTypePublic[]; total?: number }>(`/asset-types${listParamsToQuery(params)}`);
}

export function getAssetType(id: string) {
  return request<{ asset_type: AssetTypePublic }>(`/asset-types/${id}`);
}

export function createAssetType(data: import("@dugads/shared").CreateAssetTypeInput & { organization_id: string }) {
  return request<{ asset_type: AssetTypePublic }>("/asset-types", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateAssetType(id: string, data: import("@dugads/shared").UpdateAssetTypeInput) {
  return request<{ asset_type: AssetTypePublic }>(`/asset-types/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteAssetType(id: string) {
  return request<{ success: boolean }>(`/asset-types/${id}`, {
    method: "DELETE",
  });
}

// Assets

export function getAssets(courtId: string) {
  return request<{ assets: AssetPublic[] }>(`/courts/${courtId}/assets`);
}

export function getAsset(id: string) {
  return request<{ asset: AssetPublic }>(`/assets/${id}`);
}

export function createAsset(courtId: string, data: import("@dugads/shared").CreateAssetInput) {
  return request<{ asset: AssetPublic }>(`/courts/${courtId}/assets`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateAsset(id: string, data: import("@dugads/shared").UpdateAssetInput) {
  return request<{ asset: AssetPublic }>(`/assets/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteAsset(id: string) {
  return request<{ success: boolean }>(`/assets/${id}`, {
    method: "DELETE",
  });
}

// Ad Spaces

export function createAdSpace(assetId: string, data: import("@dugads/shared").CreateAdSpaceInput) {
  return request<{ ad_space: AdSpacePublic }>(`/assets/${assetId}/ad-spaces`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateAdSpace(id: string, data: import("@dugads/shared").UpdateAdSpaceInput) {
  return request<{ ad_space: AdSpacePublic }>(`/ad-spaces/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteAdSpace(id: string) {
  return request<{ success: boolean }>(`/ad-spaces/${id}`, {
    method: "DELETE",
  });
}

// Placements

export function getPlacements(params?: { status?: string }) {
  const qs = new URLSearchParams();
  if (params?.status) qs.set("status", params.status);
  const query = qs.toString();
  return request<{ placements: PlacementPublic[] }>(
    `/placements${query ? `?${query}` : ""}`,
  );
}

export function getPlacement(id: string) {
  return request<{ placement: PlacementPublic }>(`/placements/${id}`);
}

export function createPlacement(data: import("@dugads/shared").CreatePlacementInput) {
  return request<{ placement: PlacementPublic }>("/placements", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updatePlacement(id: string, data: import("@dugads/shared").UpdatePlacementInput) {
  return request<{ placement: PlacementPublic }>(`/placements/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deletePlacement(id: string) {
  return request<{ success: boolean }>(`/placements/${id}`, {
    method: "DELETE",
  });
}

export function getAdSpaces() {
  return request<{
    ad_spaces: Array<{
      id: string;
      asset_id: string;
      name: string;
      width_mm: number;
      height_mm: number;
      asset_name: string | null;
      court_name: string;
      school_name: string;
      placement_count: number;
    }>;
  }>("/ad-spaces");
}

export function getSponsorPlacements(sponsorId: string) {
  return request<{ placements: PlacementPublic[] }>(`/sponsors/${sponsorId}/placements`);
}

// Teams

export function getTeams(schoolId: string, ageGroupId?: string) {
  const qs = ageGroupId ? `?age_group_id=${ageGroupId}` : "";
  return request<import("@dugads/shared").TeamListResponse>(`/schools/${schoolId}/teams${qs}`);
}

export function getAllTeams(params?: ListParams) {
  return request<import("@dugads/shared").TeamListResponse & { total: number }>(
    `/teams${listParamsToQuery(params)}`,
  );
}

export function getTeam(id: string) {
  return request<import("@dugads/shared").TeamResponse>(`/teams/${id}`);
}

export function createTeam(schoolId: string, data: import("@dugads/shared").CreateTeamInput) {
  return request<import("@dugads/shared").TeamResponse>(`/schools/${schoolId}/teams`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function reorderTeams(schoolId: string, ageGroupId: string, teamIds: string[]) {
  return request<import("@dugads/shared").TeamListResponse>(`/schools/${schoolId}/teams/reorder`, {
    method: "PUT",
    body: JSON.stringify({ age_group_id: ageGroupId, team_ids: teamIds }),
  });
}

export function updateTeam(id: string, data: import("@dugads/shared").UpdateTeamInput) {
  return request<import("@dugads/shared").TeamResponse>(`/teams/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteTeam(id: string) {
  return request<{ success: boolean }>(`/teams/${id}`, {
    method: "DELETE",
  });
}

export function getTeamDeleteImpact(id: string) {
  return request<import("@dugads/shared").TeamDeleteImpactResponse>(`/teams/${id}/delete-impact`);
}

// Team Roster

export function getTeamPlayers(teamId: string) {
  return request<import("@dugads/shared").TeamRosterResponse>(`/teams/${teamId}/players`);
}

export function addTeamPlayer(teamId: string, playerId: string, season?: string) {
  return request<{ player: import("@dugads/shared").TeamPlayerPublic }>(`/teams/${teamId}/players`, {
    method: "POST",
    body: JSON.stringify({ player_id: playerId, season }),
  });
}

export function removeTeamPlayer(teamId: string, playerId: string) {
  return request<{ success: boolean }>(`/teams/${teamId}/players/${playerId}`, {
    method: "DELETE",
  });
}

export function bulkAssignPlayers(teamId: string, playerIds: string[]) {
  return request<import("@dugads/shared").BulkAssignPlayersResponse>(`/teams/${teamId}/players/bulk`, {
    method: "POST",
    body: JSON.stringify({ player_ids: playerIds }),
  });
}

export function updatePlayerRole(teamId: string, playerId: string, role: import("@dugads/shared").TeamPlayerRole) {
  return request<{ player: import("@dugads/shared").TeamPlayerPublic }>(`/teams/${teamId}/players/${playerId}`, {
    method: "PUT",
    body: JSON.stringify({ role }),
  });
}

export function convertTrialTeam(teamId: string) {
  return request<import("@dugads/shared").TeamResponse>(`/teams/${teamId}/convert`, {
    method: "PUT",
  });
}

// Coaches

export function getCoaches(schoolId: string, params?: ListParams) {
  return request<import("@dugads/shared").CoachListResponse & { total?: number }>(
    `/schools/${schoolId}/coaches${listParamsToQuery(params)}`,
  );
}

export function getCoach(id: string) {
  return request<import("@dugads/shared").CoachResponse>(`/coaches/${id}`);
}

export function createCoach(schoolId: string, userId: string) {
  return request<import("@dugads/shared").CoachResponse>(`/schools/${schoolId}/coaches`, {
    method: "POST",
    body: JSON.stringify({ user_id: userId }),
  });
}

export function deleteCoach(schoolId: string, coachId: string) {
  return request<{ success: boolean }>(`/schools/${schoolId}/coaches/${coachId}`, {
    method: "DELETE",
  });
}

// Coach-Team Assignment

export function getTeamCoaches(teamId: string) {
  return request<import("@dugads/shared").TeamCoachesResponse>(`/teams/${teamId}/coaches`);
}

export function assignCoachToTeam(teamId: string, coachId: string) {
  return request<{ coach: import("@dugads/shared").CoachTeamPublic }>(`/teams/${teamId}/coaches`, {
    method: "POST",
    body: JSON.stringify({ coach_id: coachId }),
  });
}

export function unassignCoachFromTeam(teamId: string, coachId: string) {
  return request<{ success: boolean }>(`/teams/${teamId}/coaches/${coachId}`, {
    method: "DELETE",
  });
}

// Team Publications (108)

export type PublishableEventOption = {
  id: string;
  name: string;
  slug: string;
  starts_at: string;
  status: string;
};

export function getPublishableEvents(teamId: string) {
  return request<{ events: PublishableEventOption[] }>(
    `/teams/${teamId}/publishable-events`,
  );
}

export function createTeamPublication(
  teamId: string,
  input: import("@dugads/shared").CreateTeamPublicationInput,
) {
  return request<import("@dugads/shared").CreateTeamPublicationResponse>(
    `/teams/${teamId}/publications`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

export function getTeamPublications(teamId: string) {
  return request<import("@dugads/shared").ListTeamPublicationsResponse>(
    `/teams/${teamId}/publications`,
  );
}

export function getEventPublications(eventId: string) {
  return request<import("@dugads/shared").ListTeamPublicationsResponse>(
    `/events/${eventId}/publications`,
  );
}

// Event Message Board (197)

export function getEventPosts(eventId: string) {
  return request<import("@dugads/shared").EventPostListResponse>(`/events/${eventId}/posts`);
}

export function createEventPost(
  eventId: string,
  input: import("@dugads/shared").CreateEventPostInput,
) {
  return request<import("@dugads/shared").EventPostResponse>(`/events/${eventId}/posts`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateEventPost(
  eventId: string,
  postId: string,
  input: import("@dugads/shared").UpdateEventPostInput,
) {
  return request<import("@dugads/shared").EventPostResponse>(`/events/${eventId}/posts/${postId}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function deleteEventPost(eventId: string, postId: string) {
  return request<{ success: true }>(`/events/${eventId}/posts/${postId}`, { method: "DELETE" });
}

// My Player (119 -- minimal player home)

export function getMePlayer() {
  return request<import("@dugads/shared").MePlayerResponse>(`/me/player`);
}

// --- Match Availability (138) ---

export function getMatchAvailability(matchId: string, teamId: string) {
  return request<import("@dugads/shared").MatchAvailabilityResponse>(
    `/matches/${matchId}/teams/${teamId}/availability`,
  );
}

export function requestMatchAvailability(matchId: string, teamId: string) {
  return request<import("@dugads/shared").MatchAvailabilityResponse>(
    `/matches/${matchId}/teams/${teamId}/availability/request`,
    { method: "POST" },
  );
}

export function nudgeMatchAvailability(matchId: string, teamId: string) {
  return request<{ nudged: number }>(
    `/matches/${matchId}/teams/${teamId}/availability/nudge`,
    { method: "POST" },
  );
}

export function respondMatchAvailability(
  matchId: string,
  body: { player_id: string; status: import("@dugads/shared").MatchAvailabilityReply; note?: string },
) {
  return request<{ player: import("@dugads/shared").MatchAvailabilityPlayer }>(
    `/matches/${matchId}/availability/respond`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export function getMyAvailabilityRequests() {
  return request<import("@dugads/shared").PendingAvailabilityResponse>(
    `/me/availability-requests`,
  );
}

// 141 -- one-tap post of a final result to the team's parents + event publication.
export function broadcastResult(matchId: string, teamId: string) {
  return request<import("@dugads/shared").ResultBroadcastResponse>(
    `/matches/${matchId}/teams/${teamId}/result-broadcast`,
    { method: "POST" },
  );
}

// --- Tournament Match Scheduler (113) ---
export function getTournament(eventId: string) {
  return request<import("@dugads/shared").TournamentResponse>(`/events/${eventId}/tournament`);
}

export function createTournament(
  eventId: string,
  body: import("@dugads/shared").CreateTournamentInput,
) {
  return request<import("@dugads/shared").TournamentResponse>(`/events/${eventId}/tournament`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateTournament(
  eventId: string,
  body: import("@dugads/shared").UpdateTournamentInput,
) {
  return request<import("@dugads/shared").TournamentResponse>(`/events/${eventId}/tournament`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function publishTournament(
  eventId: string,
  body: import("@dugads/shared").PublishTournamentInput,
) {
  return request<import("@dugads/shared").TournamentResponse>(
    `/events/${eventId}/tournament/publish`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export function deleteTournament(eventId: string) {
  return request<{ ok: boolean }>(`/events/${eventId}/tournament`, { method: "DELETE" });
}

// --- Tennis Scoring (115) ---
export function getTennisScore(matchId: string) {
  return request<import("@dugads/shared").TennisScoreResponse>(`/tennis/matches/${matchId}/score`);
}
export function recordTennisPoint(matchId: string, point_winner: "home" | "away") {
  const path = `/tennis/matches/${matchId}/score-events`;
  const body = { point_winner };
  return queueableWrite(
    { sport: "tennis", op: "record", matchId, label: `Point (${point_winner})`, method: "POST", path, body },
    () =>
      request<import("@dugads/shared").TennisScoreResponse>(path, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  );
}
export function undoTennisPoint(matchId: string, eventId: string) {
  const path = `/tennis/matches/${matchId}/score-events/${eventId}`;
  return queueableWrite(
    { sport: "tennis", op: "undo", matchId, label: "Undo point", method: "DELETE", path, body: null },
    () => request<import("@dugads/shared").TennisScoreResponse>(path, { method: "DELETE" }),
  );
}
export function finalizeTennis(matchId: string) {
  const path = `/tennis/matches/${matchId}/finalize`;
  return queueableWrite(
    { sport: "tennis", op: "finalize", matchId, label: "Finalize", method: "POST", path, body: null },
    () => request<import("@dugads/shared").TennisScoreResponse>(path, { method: "POST" }),
  );
}

// --- Cricket Scoring (116) ---
export function getCricketScore(matchId: string) {
  return request<import("@dugads/shared").CricketScoreResponse>(`/cricket/matches/${matchId}/score`);
}
export function recordCricketEvent(matchId: string, body: { kind: "runs" | "wicket"; runs?: number }) {
  const path = `/cricket/matches/${matchId}/score-events`;
  const label = body.kind === "wicket" ? "Wicket" : `${body.runs ?? 0} run(s)`;
  return queueableWrite(
    { sport: "cricket", op: "record", matchId, label, method: "POST", path, body },
    () =>
      request<import("@dugads/shared").CricketScoreResponse>(path, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  );
}
export function undoCricketEvent(matchId: string, eventId: string) {
  const path = `/cricket/matches/${matchId}/score-events/${eventId}`;
  return queueableWrite(
    { sport: "cricket", op: "undo", matchId, label: "Undo ball", method: "DELETE", path, body: null },
    () => request<import("@dugads/shared").CricketScoreResponse>(path, { method: "DELETE" }),
  );
}
export function finalizeCricket(matchId: string) {
  const path = `/cricket/matches/${matchId}/finalize`;
  return queueableWrite(
    { sport: "cricket", op: "finalize", matchId, label: "Finalize", method: "POST", path, body: null },
    () => request<import("@dugads/shared").CricketScoreResponse>(path, { method: "POST" }),
  );
}

// --- Athletics Scoring (117) ---
type AthleticsResp = import("@dugads/shared").AthleticsScoreResponse;
export function getAthleticsScore(matchId: string) {
  return request<AthleticsResp>(`/athletics/matches/${matchId}/score`);
}
export function createAthleticsCompetition(matchId: string, body: import("@dugads/shared").CreateCompetitionInput) {
  return request<AthleticsResp>(`/athletics/matches/${matchId}/competitions`, { method: "POST", body: JSON.stringify(body) });
}
export function deleteAthleticsCompetition(competitionId: string) {
  return request<AthleticsResp>(`/athletics/competitions/${competitionId}`, { method: "DELETE" });
}
export function addAthleticsEntry(competitionId: string, body: import("@dugads/shared").UpsertEntryInput) {
  return request<AthleticsResp>(`/athletics/competitions/${competitionId}/entries`, { method: "POST", body: JSON.stringify(body) });
}
export function updateAthleticsEntry(entryId: string, body: Partial<import("@dugads/shared").UpsertEntryInput>) {
  return request<AthleticsResp>(`/athletics/entries/${entryId}`, { method: "PATCH", body: JSON.stringify(body) });
}
export function deleteAthleticsEntry(entryId: string) {
  return request<AthleticsResp>(`/athletics/entries/${entryId}`, { method: "DELETE" });
}

// --- Decathlon Scoring (118) ---
type DecResp = import("@dugads/shared").DecathlonResponse;
export function getDecathlonScore(matchId: string) {
  return request<DecResp>(`/decathlon/matches/${matchId}/score`);
}
export function addDecathlonAthlete(matchId: string, body: import("@dugads/shared").AddDecathlonAthleteInput) {
  return request<DecResp>(`/decathlon/matches/${matchId}/athletes`, { method: "POST", body: JSON.stringify(body) });
}
export function updateDecathlonAthlete(athleteId: string, body: import("@dugads/shared").UpdateDecathlonAthleteInput) {
  return request<DecResp>(`/decathlon/athletes/${athleteId}`, { method: "PATCH", body: JSON.stringify(body) });
}
export function deleteDecathlonAthlete(athleteId: string) {
  return request<DecResp>(`/decathlon/athletes/${athleteId}`, { method: "DELETE" });
}
export function recordDecathlonResult(athleteId: string, body: import("@dugads/shared").RecordDecathlonResultInput) {
  return request<DecResp>(`/decathlon/athletes/${athleteId}/results`, { method: "POST", body: JSON.stringify(body) });
}

// --- Inter-School Events (114) ---
export function inviteSchoolToEvent(eventId: string, schoolId: string) {
  return request<{ ok: boolean; status: string }>(
    `/events/${eventId}/schools/${schoolId}/invitation`,
    { method: "POST" },
  );
}

export function respondFixtureInvite(eventId: string, schoolId: string, status: "accepted" | "declined") {
  return request<{ ok: boolean; status: string }>(
    `/events/${eventId}/schools/${schoolId}/invitation`,
    { method: "PATCH", body: JSON.stringify({ status }) },
  );
}

export function getFixtureInvites() {
  return request<import("@dugads/shared").FixtureInvitesResponse>(`/me/fixture-invites`);
}

// --- Coach Inbox / Conversations (140) ---

export function getMyConversations() {
  return request<import("@dugads/shared").ConversationListResponse>(`/me/conversations`);
}

export function getConversation(id: string) {
  return request<import("@dugads/shared").ConversationDetailResponse>(`/conversations/${id}`);
}

export function startConversation(body: import("@dugads/shared").StartConversationInput) {
  return request<{ id: string }>(`/conversations`, { method: "POST", body: JSON.stringify(body) });
}

export function sendConversationMessage(id: string, body: { body: string }) {
  return request<{ id: string }>(`/conversations/${id}/messages`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// Role-composed landing dashboard. Optional schoolId scopes the staff block
// for multi-school admins.
export function getMeDashboard(schoolId?: string) {
  const qs = schoolId ? `?school_id=${encodeURIComponent(schoolId)}` : "";
  return request<import("@dugads/shared").MeDashboardResponse>(`/me/dashboard${qs}`);
}

// Parent Linking

export function linkChild(familyCode: string) {
  return request<import("@dugads/shared").LinkedChildrenResponse>("/link", {
    method: "POST",
    body: JSON.stringify({ family_code: familyCode }),
  });
}

export function getMyChildren() {
  return request<import("@dugads/shared").LinkedChildrenResponse>("/me/children");
}

export function getChildTeams(playerId: string) {
  return request<import("@dugads/shared").ChildTeamsResponse>(`/me/children/${playerId}/teams`);
}

// 189 -- parent creates a child from scratch (Path B)
export function createChild(data: import("@dugads/shared").CreateChildInput) {
  return request<{ player_id: string }>("/me/children", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// 190 -- parent edits their child's identity (notify-only)
export function updateChild(playerId: string, data: import("@dugads/shared").UpdateChildInput) {
  return request<{ player_id: string }>(`/me/children/${playerId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

// 188/189 -- enrollment requests (parent self-serve sport signup + coach approval)
export function getChildEnrollmentRequests(playerId: string) {
  return request<{ requests: import("@dugads/shared").EnrollmentRequestPublic[] }>(
    `/me/children/${playerId}/enrollment-requests`,
  );
}

export function submitEnrollmentRequest(playerId: string, data: import("@dugads/shared").SubmitEnrollmentRequestInput) {
  return request<{ request: import("@dugads/shared").EnrollmentRequestPublic }>(
    `/me/children/${playerId}/enrollment-requests`,
    { method: "POST", body: JSON.stringify(data) },
  );
}

export function updateEnrollmentIndemnity(playerId: string, id: string, data: import("@dugads/shared").UpdateEnrollmentIndemnityInput) {
  return request<{ request: import("@dugads/shared").EnrollmentRequestPublic }>(
    `/me/children/${playerId}/enrollment-requests/${id}/indemnity`,
    { method: "PATCH", body: JSON.stringify(data) },
  );
}

export function withdrawEnrollmentRequest(playerId: string, id: string) {
  return request<{ request: import("@dugads/shared").EnrollmentRequestPublic }>(
    `/me/children/${playerId}/enrollment-requests/${id}/withdraw`,
    { method: "POST" },
  );
}

// 190 -- coach approval queue
export function getCoachEnrollmentRequests() {
  return request<{ requests: import("@dugads/shared").CoachEnrollmentRequestPublic[] }>(
    `/coach/enrollment-requests`,
  );
}

export function approveEnrollmentRequest(id: string, data: import("@dugads/shared").ApproveEnrollmentRequestInput) {
  return request<{ request: import("@dugads/shared").EnrollmentRequestPublic }>(
    `/coach/enrollment-requests/${id}/approve`,
    { method: "POST", body: JSON.stringify(data) },
  );
}

export function declineEnrollmentRequest(id: string, data: import("@dugads/shared").DeclineEnrollmentRequestInput) {
  return request<{ request: import("@dugads/shared").EnrollmentRequestPublic }>(
    `/coach/enrollment-requests/${id}/decline`,
    { method: "POST", body: JSON.stringify(data) },
  );
}

// --- Child-data guardian consent (143) ---

export function getChildrenConsentStatus() {
  return request<import("@dugads/shared").ChildrenConsentStatusResponse>(
    `/me/children/consent-status`,
  );
}

export function recordChildConsent(playerId: string, version: string) {
  return request<{ consent: import("@dugads/shared").ChildConsentPublic }>(
    `/me/children/${playerId}/consent`,
    { method: "POST", body: JSON.stringify({ version }) },
  );
}

export function withdrawChildConsent(playerId: string) {
  return request<{ success: boolean }>(`/me/children/${playerId}/consent`, {
    method: "DELETE",
  });
}

export function unlinkChild(playerId: string) {
  return request<{ success: boolean }>(`/me/children/${playerId}`, {
    method: "DELETE",
  });
}

// Court Templates

export function getCourtTemplates(courtTypeId: string) {
  return request<import("@dugads/shared").CourtTemplateListResponse>(
    `/court-types/${courtTypeId}/templates`,
  );
}

export function getCourtTemplate(id: string) {
  return request<import("@dugads/shared").CourtTemplateResponse>(
    `/court-templates/${id}`,
  );
}

export function createCourtTemplate(
  courtTypeId: string,
  data: { name: string; is_default?: boolean },
) {
  return request<import("@dugads/shared").CourtTemplateResponse>(
    `/court-types/${courtTypeId}/templates`,
    { method: "POST", body: JSON.stringify(data) },
  );
}

export function updateCourtTemplate(
  id: string,
  data: { name?: string; is_default?: boolean },
) {
  return request<import("@dugads/shared").CourtTemplateResponse>(
    `/court-templates/${id}`,
    { method: "PUT", body: JSON.stringify(data) },
  );
}

export function deleteCourtTemplate(id: string) {
  return request<{ success: boolean }>(`/court-templates/${id}`, {
    method: "DELETE",
  });
}

export function copyCourtTemplate(id: string) {
  return request<import("@dugads/shared").CourtTemplateResponse>(
    `/court-templates/${id}/copy`,
    { method: "POST" },
  );
}

// Packages

export function getPackages(params?: ListParams) {
  return request<import("@dugads/shared").PackageListResponse & { total?: number }>(`/packages${listParamsToQuery(params)}`);
}

export function getPackage(id: string) {
  return request<import("@dugads/shared").PackageDetailResponse>(`/packages/${id}`);
}

export function createPackage(data: import("@dugads/shared").CreatePackageInput) {
  return request<import("@dugads/shared").PackageResponse>("/packages", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updatePackage(id: string, data: import("@dugads/shared").UpdatePackageInput) {
  return request<import("@dugads/shared").PackageResponse>(`/packages/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deletePackage(id: string) {
  return request<{ success: boolean }>(`/packages/${id}`, {
    method: "DELETE",
  });
}

// Events

export function getAllEvents(params?: { type?: string; when?: string; school_id?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.type) searchParams.set("type", params.type);
  if (params?.when) searchParams.set("when", params.when);
  if (params?.school_id) searchParams.set("school_id", params.school_id);
  const qs = searchParams.toString();
  return request<import("@dugads/shared").EventListResponse>(
    `/events${qs ? `?${qs}` : ""}`,
  );
}

export function getEvents(schoolId: string, params?: ListParams & { type?: string; when?: string }) {
  const qs = listParamsToQuery(params, { type: params?.type, when: params?.when });
  return request<import("@dugads/shared").EventListResponse & { total?: number }>(
    `/schools/${schoolId}/events${qs}`,
  );
}

export function getCalendar(
  schoolId: string,
  start: string,
  end: string,
  filters?: { my_events?: boolean; include_cancelled?: boolean },
) {
  const params = new URLSearchParams({ start, end });
  if (filters?.my_events) params.set("my_events", "true");
  if (filters?.include_cancelled) params.set("include_cancelled", "true");
  return request<import("@dugads/shared").CalendarResponse>(
    `/schools/${schoolId}/calendar?${params.toString()}`,
  );
}

// 130 -- aggregated parent feed: kids'-participation + followed-school events,
// with children (incl. school_id) + available sports. Powers the spectator
// filter's "My Kids" / "My Favourites" / "My Schools" chips on web.
export function getMyCalendar(
  start: string,
  end: string,
  params?: { sport?: string; type?: string; child_id?: string; include_cancelled?: boolean },
) {
  const search = new URLSearchParams({ start, end });
  if (params?.sport) search.set("sport", params.sport);
  if (params?.type) search.set("type", params.type);
  if (params?.child_id) search.set("child_id", params.child_id);
  if (params?.include_cancelled) search.set("include_cancelled", "true");
  return request<import("@dugads/shared").MyCalendarResponse>(
    `/me/calendar?${search.toString()}`,
  );
}

export function getEvent(id: string) {
  return request<import("@dugads/shared").EventResponse>(`/events/${id}`);
}

export function createEvent(schoolId: string, data: { sport_id?: string | null; name: string; event_type: string; starts_at: string; ends_at: string; age_group_id?: string | null }) {
  return request<import("@dugads/shared").EventResponse>(`/schools/${schoolId}/events`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateEvent(id: string, data: import("@dugads/shared").UpdateEventInput) {
  return request<import("@dugads/shared").EventResponse>(`/events/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteEvent(id: string) {
  return request<{ success: boolean }>(`/events/${id}`, {
    method: "DELETE",
  });
}

// 080 -- lifecycle actions
// 191 -- acknowledge_conflicts republishes past the same-field overlap 409
// (code: schedule_conflicts) after the user confirms the warning dialog.
export function publishEvent(id: string, body?: { notify?: boolean; acknowledge_conflicts?: boolean }) {
  return request<import("@dugads/shared").EventResponse>(`/events/${id}/publish`, {
    method: "POST",
    body: JSON.stringify(body ?? {}),
  });
}

export function cancelEvent(id: string, body: { reason: string; notify?: boolean }) {
  return request<import("@dugads/shared").EventResponse>(`/events/${id}/cancel`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function unpublishEvent(id: string) {
  return request<import("@dugads/shared").EventResponse>(`/events/${id}/unpublish`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function getSchoolDraftEvents(schoolId: string) {
  return request<import("@dugads/shared").EventListResponse>(`/schools/${schoolId}/events/drafts`);
}

// 193 -- bulk time shift (run-late shift-from-here / multi-select move).
// Body is one of two selectors, validated by the shared shiftMatchesSchema.
export function shiftMatches(eventId: string, body: import("@dugads/shared").ShiftMatchesInput) {
  return request<{ shifted: number }>(`/events/${eventId}/matches/shift`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// 192 -- match day templates (reusable hosting-day layouts, school-scoped)

export function getMatchDayTemplates(schoolId: string) {
  return request<import("@dugads/shared").MatchDayTemplateListResponse>(`/schools/${schoolId}/match-day-templates`);
}

export function updateMatchDayTemplate(id: string, body: import("@dugads/shared").UpdateMatchDayTemplateInput) {
  return request<import("@dugads/shared").MatchDayTemplateResponse>(`/match-day-templates/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function deleteMatchDayTemplate(id: string) {
  return request<{ success: boolean }>(`/match-day-templates/${id}`, { method: "DELETE" });
}

export function saveEventAsTemplate(eventId: string, body: { name: string; overwrite?: boolean }) {
  return request<import("@dugads/shared").MatchDayTemplateResponse>(`/events/${eventId}/save-as-template`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function applyTemplateToEvent(eventId: string, body: { template_id: string; date: string }) {
  return request<import("@dugads/shared").ApplyMatchDayTemplateResponse>(`/events/${eventId}/apply-template`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// Event Schools

export function getEventSchools(eventId: string) {
  return request<{ schools: import("@dugads/shared").EventSchoolPublic[] }>(`/events/${eventId}/schools`);
}

export function addEventSchool(eventId: string, data: { school_id: string; is_host?: boolean }) {
  return request<{ school: import("@dugads/shared").EventSchoolPublic }>(`/events/${eventId}/schools`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateEventSchool(eventId: string, schoolEntryId: string, data: { is_host?: boolean; team_names?: string[] }) {
  return request<{ school: import("@dugads/shared").EventSchoolPublic }>(`/events/${eventId}/schools/${schoolEntryId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function removeEventSchool(eventId: string, schoolEntryId: string) {
  return request<{ success: boolean }>(`/events/${eventId}/schools/${schoolEntryId}`, {
    method: "DELETE",
  });
}

// Event Courts

export function getEventCourts(eventId: string) {
  return request<{ courts: import("@dugads/shared").EventCourtPublic[] }>(`/events/${eventId}/courts`);
}

export function addEventCourt(
  eventId: string,
  data: { court_id: string; event_venue_id?: string } | { name: string; event_venue_id?: string },
) {
  return request<{ court: import("@dugads/shared").EventCourtPublic }>(`/events/${eventId}/courts`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function removeEventCourt(eventId: string, courtEntryId: string) {
  return request<{ success: boolean }>(`/events/${eventId}/courts/${courtEntryId}`, {
    method: "DELETE",
  });
}

// Matches

export function getMatches(eventId: string) {
  return request<import("@dugads/shared").MatchListResponse>(`/events/${eventId}/matches`);
}

export function getEventTeams(eventId: string) {
  return request<import("@dugads/shared").TeamListResponse>(`/events/${eventId}/teams`);
}

export function getMatch(id: string) {
  return request<import("@dugads/shared").MatchResponse>(`/matches/${id}`);
}

export function createMatch(eventId: string, data: { event_court_id?: string; event_venue_id?: string; sport_id?: string; name: string; starts_at: string; age_group?: string; first_half_mins?: number; break_mins?: number; second_half_mins?: number }) {
  return request<import("@dugads/shared").MatchResponse>(`/events/${eventId}/matches`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateMatch(id: string, data: import("@dugads/shared").UpdateMatchInput) {
  return request<import("@dugads/shared").MatchResponse>(`/matches/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteMatch(id: string) {
  return request<{ success: boolean }>(`/matches/${id}`, {
    method: "DELETE",
  });
}

// Match Teams

export function getMatchTeams(matchId: string) {
  return request<import("@dugads/shared").MatchTeamListResponse>(`/matches/${matchId}/teams`);
}

export function addMatchTeam(matchId: string, data: import("@dugads/shared").AddMatchTeamInput) {
  return request<import("@dugads/shared").MatchTeamResponse>(`/matches/${matchId}/teams`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function removeMatchTeam(matchId: string, teamId: string) {
  return request<{ success: boolean }>(`/matches/${matchId}/teams/${teamId}`, {
    method: "DELETE",
  });
}

// Lineups

export function getMatchLineup(matchId: string, teamId: string) {
  return request<import("@dugads/shared").MatchLineupResponse>(
    `/matches/${matchId}/teams/${teamId}/lineup`,
  );
}

export function getMatchLineups(matchId: string) {
  return request<import("@dugads/shared").MatchLineupsResponse>(
    `/matches/${matchId}/lineup`,
  );
}

// 139 -- availability + eligibility pool that seeds the lineup builder.
export function getSelectionPool(matchId: string, teamId: string) {
  return request<import("@dugads/shared").SelectionPoolResponse>(
    `/matches/${matchId}/teams/${teamId}/selection-pool`,
  );
}

export function updateMatchLineup(
  matchId: string,
  teamId: string,
  data: import("@dugads/shared").UpsertLineupInput,
) {
  return request<import("@dugads/shared").MatchLineupResponse>(
    `/matches/${matchId}/teams/${teamId}/lineup`,
    { method: "PUT", body: JSON.stringify(data) },
  );
}

export function deleteMatchLineup(matchId: string, teamId: string) {
  return request<{ success: boolean }>(
    `/matches/${matchId}/teams/${teamId}/lineup`,
    { method: "DELETE" },
  );
}

// 154 -- Match officials + BokSmart form
export function getMatchOfficials(matchId: string) {
  return request<import("@dugads/shared").MatchOfficialsResponse>(
    `/matches/${matchId}/officials`,
  );
}

export function updateMatchOfficials(
  matchId: string,
  data: import("@dugads/shared").UpsertMatchOfficialsInput,
) {
  return request<import("@dugads/shared").MatchOfficialsResponse>(
    `/matches/${matchId}/officials`,
    { method: "PUT", body: JSON.stringify(data) },
  );
}

export function getBokSmartForm(matchId: string) {
  return request<import("@dugads/shared").BokSmartFormData>(
    `/matches/${matchId}/boksmart`,
  );
}

// Team Positions

export function getTeamPositions(teamId: string) {
  return request<import("@dugads/shared").TeamPositionResponse>(
    `/teams/${teamId}/positions`,
  );
}

export function setPlayerPosition(
  teamId: string,
  playerId: string,
  data: import("@dugads/shared").SetPlayerPositionInput,
) {
  return request<import("@dugads/shared").TeamPositionResponse>(
    `/teams/${teamId}/players/${playerId}/position`,
    { method: "PUT", body: JSON.stringify(data) },
  );
}

export function deletePlayerPosition(teamId: string, playerId: string) {
  return request<import("@dugads/shared").TeamPositionResponse>(
    `/teams/${teamId}/players/${playerId}/position`,
    { method: "DELETE" },
  );
}

export function deleteTeamPositions(teamId: string) {
  return request<{ success: boolean }>(
    `/teams/${teamId}/positions`,
    { method: "DELETE" },
  );
}


// Sponsors

export function getSponsors(params?: ListParams) {
  return request<import("@dugads/shared").SponsorListResponse & { total?: number }>(`/sponsors${listParamsToQuery(params)}`);
}

export function getSponsor(id: string) {
  return request<import("@dugads/shared").SponsorResponse>(`/sponsors/${id}`);
}

export function createSponsor(data: import("@dugads/shared").CreateSponsorInput) {
  return request<import("@dugads/shared").SponsorResponse>("/sponsors", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateSponsor(id: string, data: import("@dugads/shared").UpdateSponsorInput) {
  return request<import("@dugads/shared").SponsorResponse>(`/sponsors/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteSponsor(id: string) {
  return request<{ success: boolean }>(`/sponsors/${id}`, {
    method: "DELETE",
  });
}

export function getSponsorUsers(id: string) {
  return request<import("@dugads/shared").SponsorUserListResponse>(`/sponsors/${id}/users`);
}

export function addSponsorUser(id: string, data: import("@dugads/shared").AddSponsorUserInput) {
  return request<{ success: boolean }>(`/sponsors/${id}/users`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateSponsorUser(sponsorId: string, userId: string, data: import("@dugads/shared").UpdateSponsorUserInput) {
  return request<{ success: boolean }>(`/sponsors/${sponsorId}/users/${userId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

export function removeSponsorUser(sponsorId: string, userId: string) {
  return request<{ success: boolean }>(`/sponsors/${sponsorId}/users/${userId}`, {
    method: "DELETE",
  });
}


// Sponsor image upload/delete

export async function uploadSponsorImage(sponsorId: string, file: File) {
  const formData = new FormData();
  formData.append("image", file);
  const token = await getIdToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}/sponsors/${sponsorId}/image`, {
    method: "POST",
    headers,
    body: formData,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.error ?? "Upload failed");
  }
  return res.json() as Promise<import("@dugads/shared").SponsorResponse>;
}

export function deleteSponsorImage(sponsorId: string) {
  return request<import("@dugads/shared").SponsorResponse>(`/sponsors/${sponsorId}/image`, {
    method: "DELETE",
  });
}

// 085 -- School logo upload/delete
export async function uploadSchoolLogo(schoolId: string, file: File) {
  const formData = new FormData();
  formData.append("logo", file);
  const token = await getIdToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}/schools/${schoolId}/logo`, {
    method: "POST",
    headers,
    body: formData,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.error ?? "Upload failed");
  }
  return res.json() as Promise<import("@dugads/shared").SchoolResponse>;
}

export function deleteSchoolLogo(schoolId: string) {
  return request<import("@dugads/shared").SchoolResponse>(`/schools/${schoolId}/logo`, {
    method: "DELETE",
  });
}

// 104 -- POST /schools/:id/logo/fetch-from-url
export function fetchSchoolLogoFromUrl(schoolId: string, url: string) {
  return request<import("@dugads/shared").SchoolResponse>(`/schools/${schoolId}/logo/fetch-from-url`, {
    method: "POST",
    body: JSON.stringify({ url }),
  });
}


// Users

export function getUsers() {
  return request<{ users: import("@dugads/shared").UserListItem[] }>("/users");
}

export function getUser(id: string) {
  return request<import("@dugads/shared").UserWithRoles>(`/users/${id}`);
}

export function createUser(data: import("@dugads/shared").CreateUserInput) {
  return request<import("@dugads/shared").CreateUserResponse>("/users", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateUser(id: string, data: import("@dugads/shared").UpdateUserInput) {
  return request<{ id: string; email: string; first_name: string | null; last_name: string | null }>(`/users/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export function deleteUser(id: string) {
  return request<{ success: boolean }>(`/users/${id}`, {
    method: "DELETE",
  });
}

export function removeUserRole(userId: string, roleId: string) {
  return request<{ success: boolean }>(`/users/${userId}/roles/${roleId}`, {
    method: "DELETE",
  });
}

export function resetUserPassword(userId: string) {
  return request<import("@dugads/shared").ResetPasswordResponse>(
    `/users/${userId}/reset-password`,
    { method: "POST" },
  );
}

export function getManagedScopes() {
  return request<import("@dugads/shared").ManagedScopesResponse>("/me/managed-scopes");
}

export function getAssignableRoles(userId: string) {
  return request<import("@dugads/shared").AssignableRolesResponse>(
    `/users/${userId}/assignable-roles`,
  );
}

export function getOrgUsers(orgId: string) {
  return request<{ users: import("@dugads/shared").UserListItem[] }>(`/organizations/${orgId}/users`);
}

export function getSchoolUsers(schoolId: string) {
  return request<{ users: import("@dugads/shared").UserListItem[] }>(`/schools/${schoolId}/users`);
}

// Context Pins

export function getPins() {
  return request<import("@dugads/shared").PinListResponse>("/me/pins");
}

export function createPin(data: import("@dugads/shared").CreatePinInput) {
  return request<{ pin: import("@dugads/shared").UserPinPublic }>("/me/pins", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function deletePin(pinId: string) {
  return request<{ success: boolean }>(`/me/pins/${pinId}`, {
    method: "DELETE",
  });
}

// Messages

export function previewMessage(data: {
  context_type: string;
  context_id: string;
  groups: string[];
  content: string;
  school_id?: string;
  subgroup?: "forwards" | "backs" | null; // 163
}) {
  return request<import("@dugads/shared").MessagePreviewResponse>(
    `/messages/preview`,
    { method: "POST", body: JSON.stringify(data) },
  );
}

export function sendMessage(data: {
  context_type: string;
  context_id: string;
  groups: string[];
  content: string;
  response_options?: import("@dugads/shared").ResponseOption[];
  document_id?: string;
  category?: "event_info" | "team" | "general"; // 163
  subgroup?: "forwards" | "backs" | null; // 163
}) {
  return request<import("@dugads/shared").SendMessageResponse>(
    `/messages`,
    { method: "POST", body: JSON.stringify(data) },
  );
}

export function getMessages(params?: { context_type?: string; context_id?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.context_type) searchParams.set("context_type", params.context_type);
  if (params?.context_id) searchParams.set("context_id", params.context_id);
  const qs = searchParams.toString();
  return request<import("@dugads/shared").MessageListResponse>(
    `/messages${qs ? `?${qs}` : ""}`,
  );
}

export function respondToMessage(messageId: string, value: string) {
  return request<import("@dugads/shared").RespondToMessageResponse>(
    `/messages/${messageId}/respond`,
    { method: "POST", body: JSON.stringify({ value }) },
  );
}

export function getMessageResponses(messageId: string) {
  return request<import("@dugads/shared").MessageResponsesResponse>(
    `/messages/${messageId}/responses`,
  );
}

export function markMessageRead(messageId: string) {
  return request<{ read_at: string }>(
    `/messages/${messageId}/read`,
    { method: "POST" },
  );
}

export function getMessageRecipients(messageId: string) {
  return request<import("@dugads/shared").MessageRecipientsResponse>(
    `/messages/${messageId}/recipients`,
  );
}

// Age Group Messages (038)

export function sendAgeGroupMessage(ageGroupId: string, data: {
  school_id: string;
  groups: string[];
  content: string;
  response_options?: import("@dugads/shared").ResponseOption[];
  document_id?: string;
}) {
  return request<import("@dugads/shared").SendMessageResponse>(
    `/age-groups/${ageGroupId}/messages`,
    { method: "POST", body: JSON.stringify(data) },
  );
}

export function getAgeGroupMessages(ageGroupId: string, schoolId: string) {
  return request<import("@dugads/shared").MessageListResponse>(
    `/age-groups/${ageGroupId}/messages?school_id=${schoolId}`,
  );
}

export function getMyMessages() {
  return request<import("@dugads/shared").ReceivedMessageListResponse>(
    `/me/messages`,
  );
}

// School Messages (041)

export function sendSchoolMessage(schoolId: string, data: {
  groups: string[];
  content: string;
  response_options?: import("@dugads/shared").ResponseOption[];
  document_id?: string;
}) {
  return request<import("@dugads/shared").SendMessageResponse>(
    `/schools/${schoolId}/messages`,
    { method: "POST", body: JSON.stringify(data) },
  );
}

export function getSchoolMessages(schoolId: string) {
  return request<import("@dugads/shared").MessageListResponse>(
    `/schools/${schoolId}/messages`,
  );
}

// Sport Messages (041)

export function sendSportMessage(sportId: string, schoolId: string, data: {
  groups: string[];
  content: string;
  response_options?: import("@dugads/shared").ResponseOption[];
  document_id?: string;
}) {
  return request<import("@dugads/shared").SendMessageResponse>(
    `/sports/${sportId}/messages?school_id=${schoolId}`,
    { method: "POST", body: JSON.stringify(data) },
  );
}

export function getSportMessages(sportId: string, schoolId: string) {
  return request<import("@dugads/shared").MessageListResponse>(
    `/sports/${sportId}/messages?school_id=${schoolId}`,
  );
}

// Direct Messages (041)

export function sendDirectMessage(data: {
  recipient_ids: string[];
  content: string;
  response_options?: import("@dugads/shared").ResponseOption[];
  document_id?: string;
  school_id: string;
}) {
  return request<import("@dugads/shared").SendMessageResponse>(
    `/messages/direct`,
    { method: "POST", body: JSON.stringify(data) },
  );
}

export function getDirectMessages() {
  return request<import("@dugads/shared").MessageListResponse>(
    `/messages/direct`,
  );
}

// Recipient Picker (041)

export function getSchoolRecipients(schoolId: string) {
  return request<import("@dugads/shared").RecipientPickerResponse>(
    `/schools/${schoolId}/recipients`,
  );
}

// Sponsor Lead Generation (067)

export function getSponsorLeadConfigs(sponsorId: string) {
  return request<import("@dugads/shared").SponsorLeadConfigListResponse>(`/sponsors/${sponsorId}/lead-config`);
}

export function createSponsorLeadConfig(sponsorId: string, data: import("@dugads/shared").CreateLeadConfigInput) {
  return request<import("@dugads/shared").SponsorLeadConfigResponse>(`/sponsors/${sponsorId}/lead-config`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateSponsorLeadConfig(sponsorId: string, configId: string, data: import("@dugads/shared").UpdateLeadConfigInput) {
  return request<import("@dugads/shared").SponsorLeadConfigResponse>(`/sponsors/${sponsorId}/lead-config/${configId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteSponsorLeadConfig(sponsorId: string, configId: string) {
  return request<void>(`/sponsors/${sponsorId}/lead-config/${configId}`, {
    method: "DELETE",
  });
}

export async function uploadLeadConfigLogo(sponsorId: string, configId: string, file: File) {
  const formData = new FormData();
  formData.append("logo", file);
  const token = await getIdToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}/sponsors/${sponsorId}/lead-config/${configId}/logo`, {
    method: "POST",
    headers,
    body: formData,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.error ?? "Upload failed");
  }
  return res.json() as Promise<import("@dugads/shared").SponsorLeadConfigResponse>;
}

export function deleteLeadConfigLogo(sponsorId: string, configId: string) {
  return request<import("@dugads/shared").SponsorLeadConfigResponse>(`/sponsors/${sponsorId}/lead-config/${configId}/logo`, {
    method: "DELETE",
  });
}

export function getSponsorLeadSchools(sponsorId: string, configId: string) {
  return request<import("@dugads/shared").SponsorLeadSchoolListResponse>(`/sponsors/${sponsorId}/lead-config/${configId}/schools`);
}

export function assignLeadSchool(sponsorId: string, configId: string, data: import("@dugads/shared").AssignLeadSchoolInput) {
  return request<{ school: import("@dugads/shared").SponsorLeadSchoolPublic }>(`/sponsors/${sponsorId}/lead-config/${configId}/schools`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function removeLeadSchool(sponsorId: string, configId: string, schoolId: string) {
  return request<void>(`/sponsors/${sponsorId}/lead-config/${configId}/schools/${schoolId}`, {
    method: "DELETE",
  });
}

export function getSponsorLeads(sponsorId: string, params?: { from?: string; to?: string; config_id?: string }) {
  const p = new URLSearchParams();
  if (params?.from) p.set("from", params.from);
  if (params?.to) p.set("to", params.to);
  if (params?.config_id) p.set("config_id", params.config_id);
  const qs = p.toString();
  return request<import("@dugads/shared").LeadSubmissionListResponse>(`/sponsors/${sponsorId}/leads${qs ? `?${qs}` : ""}`);
}

export function getSchoolLeads(schoolId: string, params?: { from?: string; to?: string }) {
  const p = new URLSearchParams();
  if (params?.from) p.set("from", params.from);
  if (params?.to) p.set("to", params.to);
  const qs = p.toString();
  return request<import("@dugads/shared").LeadSubmissionListResponse>(`/schools/${schoolId}/leads${qs ? `?${qs}` : ""}`);
}

export function getSchoolSponsorAds(schoolId: string) {
  return request<import("@dugads/shared").SponsorAdListResponse>(`/schools/${schoolId}/sponsor-ads`);
}

export function getMySponsorAds() {
  return request<import("@dugads/shared").SponsorAdListResponse>(`/me/sponsor-ads`);
}

export function submitLead(data: import("@dugads/shared").SubmitLeadInput) {
  return request<import("@dugads/shared").LeadSubmitResponse>("/leads", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// Sponsor Dashboard

export function getSponsorDashboard() {
  return request<import("@dugads/shared").SponsorDashboardResponse>("/me/sponsor/dashboard");
}

export function getSponsorDashboardPlacements() {
  return request<import("@dugads/shared").SponsorPlacementsResponse>("/me/sponsor/placements");
}

export function getSponsorDashboardSchools() {
  return request<import("@dugads/shared").SponsorSchoolsResponse>("/me/sponsor/schools");
}

export function getSponsorDashboardTimeline(months = 6) {
  return request<import("@dugads/shared").SponsorTimelineResponse>(`/me/sponsor/timeline?months=${months}`);
}

// Zones

export function getZones(schoolId: string) {
  return request<import("@dugads/shared").ZoneListResponse>(`/schools/${schoolId}/zones`);
}

export function getZone(id: string) {
  return request<import("@dugads/shared").ZoneResponse>(`/zones/${id}`);
}

export function createZone(schoolId: string, data: import("@dugads/shared").CreateZoneInput) {
  return request<import("@dugads/shared").ZoneResponse>(`/schools/${schoolId}/zones`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateZone(id: string, data: import("@dugads/shared").UpdateZoneInput) {
  return request<import("@dugads/shared").ZoneResponse>(`/zones/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteZone(id: string) {
  return request<{ success: boolean }>(`/zones/${id}`, { method: "DELETE" });
}

// Live Coaches

export function getLiveCoaches(schoolId: string) {
  return request<import("@dugads/shared").SchoolLiveCoachesResponse>(`/schools/${schoolId}/live-coaches`);
}

export function getEventCoaches(eventId: string) {
  return request<import("@dugads/shared").LiveCoachesResponse>(`/events/${eventId}/coaches`);
}

// Court Advertising Tree (3D viewer)

export function getCourtAdvertising(courtId: string) {
  return request<import("../components/field-3d/types").CourtAdvertisingResponse>(
    `/courts/${courtId}/advertising`,
  );
}

// Notifications

export interface NotificationPublic {
  id: string;
  type: string;
  title: string;
  body: string;
  data: Record<string, string> | null;
  read_at: string | null;
  created_at: string;
}

export function getNotifications() {
  return request<{ notifications: NotificationPublic[] }>("/me/notifications");
}

export function markNotificationRead(id: string) {
  return request<{ success: boolean }>(`/me/notifications/${id}/read`, {
    method: "POST",
  });
}

export function markAllNotificationsRead() {
  return request<{ success: boolean }>("/me/notifications/read-all", {
    method: "POST",
  });
}

// Age Groups

export function getAgeGroups(schoolId: string, sportId: string) {
  return request<import("@dugads/shared").AgeGroupListResponse>(`/schools/${schoolId}/sports/${sportId}/age-groups`);
}

export function getAllAgeGroups() {
  return request<import("@dugads/shared").AgeGroupListResponse>(`/age-groups`);
}

// Age groups are now read-only lookup data (no create/update/delete)

export function getAgeGroupPlayers(ageGroupId: string, schoolId: string) {
  return request<import("@dugads/shared").AgeGroupPlayerPoolResponse>(`/age-groups/${ageGroupId}/players?school=${schoolId}`);
}

export function updateAgeGroup(ageGroupId: string, data: { default_first_half_mins?: number; default_break_mins?: number; default_second_half_mins?: number }) {
  return request<import("@dugads/shared").AgeGroupResponse>(`/age-groups/${ageGroupId}`, { method: "PATCH", body: JSON.stringify(data) });
}

// Device Tokens

export function registerDeviceToken(token: string, platform: string) {
  return request<{ success: boolean }>("/me/device-tokens", {
    method: "POST",
    body: JSON.stringify({ token, platform }),
  });
}

export function deleteDeviceToken(token: string) {
  return request<{ success: boolean }>("/me/device-tokens", {
    method: "DELETE",
    body: JSON.stringify({ token }),
  });
}

// Documents (042)

export async function uploadDocument(
  schoolId: string,
  file: File,
  category: string,
  description?: string,
): Promise<import("@dugads/shared").DocumentDetailResponse> {
  const token = await getIdToken();
  const form = new FormData();
  form.append("file", file);
  form.append("category", category);
  if (description) form.append("description", description);

  const res = await fetch(`${API_BASE}/schools/${schoolId}/documents`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.error ?? "Upload failed");
  }
  return res.json();
}

export function getSchoolDocuments(schoolId: string, category?: string, pinned?: boolean) {
  const params = new URLSearchParams();
  if (category) params.set("category", category);
  if (pinned !== undefined) params.set("pinned", String(pinned));
  const qs = params.toString();
  return request<import("@dugads/shared").DocumentListResponse>(
    `/schools/${schoolId}/documents${qs ? `?${qs}` : ""}`,
  );
}

export function getDocument(id: string) {
  return request<import("@dugads/shared").DocumentDetailResponse>(`/documents/${id}`);
}

export function deleteDocument(id: string) {
  return request<{ success: boolean }>(`/documents/${id}`, { method: "DELETE" });
}

export function toggleDocumentPin(id: string) {
  return request<import("@dugads/shared").DocumentDetailResponse>(`/documents/${id}/pin`, {
    method: "PATCH",
  });
}

export function getDocumentRecipients(id: string) {
  return request<import("@dugads/shared").DocumentRecipientListResponse>(
    `/documents/${id}/recipients`,
  );
}

export function getMyDocuments() {
  return request<import("@dugads/shared").DocumentListResponse>("/me/documents");
}

export function getDocumentDownloadUrl(id: string) {
  return `${API_BASE}/documents/${id}/download`;
}

// ─── School Search (directory) ──────────────────────────────────

export function searchSchools(q: string, params?: { province?: string; phase?: string; sector?: string; affiliated?: string; organization_id?: string; sort?: string; dir?: string; page?: number; limit?: number }) {
  const p = new URLSearchParams({ q });
  if (params?.province) p.set("province", params.province);
  if (params?.phase) p.set("phase", params.phase);
  if (params?.sector) p.set("sector", params.sector);
  if (params?.affiliated) p.set("affiliated", params.affiliated);
  if (params?.organization_id) p.set("organization_id", params.organization_id);
  if (params?.sort) p.set("sort", params.sort);
  if (params?.dir) p.set("dir", params.dir);
  if (params?.page) p.set("page", String(params.page));
  p.set("limit", String(params?.limit ?? 20));
  return request<import("@dugads/shared").SchoolSearchResponse>(`/schools/search?${p}`);
}

export function getSchoolOpponents(schoolId: string) {
  return request<import("@dugads/shared").SchoolOpponentListResponse>(`/schools/${schoolId}/opponents`);
}

export function addSchoolOpponent(schoolId: string, opponentSchoolId: string) {
  return request<{ opponent: import("@dugads/shared").SchoolOpponentPublic }>(`/schools/${schoolId}/opponents`, {
    method: "POST",
    body: JSON.stringify({ opponent_school_id: opponentSchoolId }),
  });
}

export function removeSchoolOpponent(schoolId: string, opponentSchoolId: string) {
  return request<{ success: boolean }>(`/schools/${schoolId}/opponents/${opponentSchoolId}`, {
    method: "DELETE",
  });
}

export function affiliateSchool(schoolId: string, organizationId: string) {
  return request<import("@dugads/shared").SchoolResponse>(`/schools/${schoolId}/affiliate`, {
    method: "POST",
    body: JSON.stringify({ organization_id: organizationId }),
  });
}

// --- Event Discovery (070) ---

export function discoverEvents(params: {
  start: string;
  end: string;
  sport?: string;
  type?: string;
  search?: string;
  province?: string;
  /** 202: comma-separated school ids or slugs (server has supported this since 070). */
  school?: string;
  page?: number;
  per_page?: number;
}) {
  const searchParams = new URLSearchParams({ start: params.start, end: params.end });
  if (params.school) searchParams.set("school", params.school);
  if (params.sport) searchParams.set("sport", params.sport);
  if (params.type) searchParams.set("type", params.type);
  if (params.search) searchParams.set("search", params.search);
  if (params.province) searchParams.set("province", params.province);
  if (params.page) searchParams.set("page", String(params.page));
  if (params.per_page) searchParams.set("per_page", String(params.per_page));
  return request<import("@dugads/shared").EventDiscoverResponse>(
    `/events/discover?${searchParams.toString()}`,
  );
}

// Parent Invites (075)

export function getParentInvites(schoolId: string, params?: {
  q?: string; status?: string; sort_by?: string; sort_dir?: string; page?: number; limit?: number;
}) {
  const sp = new URLSearchParams();
  if (params?.q) sp.set("q", params.q);
  if (params?.status) sp.set("status", params.status);
  if (params?.sort_by) sp.set("sort_by", params.sort_by);
  if (params?.sort_dir) sp.set("sort_dir", params.sort_dir);
  if (params?.page) sp.set("page", String(params.page));
  if (params?.limit) sp.set("limit", String(params.limit));
  const qs = sp.toString();
  return request<import("@dugads/shared").ParentInviteListResponse>(
    `/schools/${schoolId}/parent-invites${qs ? `?${qs}` : ""}`,
  );
}

export function getParentInvite(schoolId: string, inviteId: string) {
  return request<import("@dugads/shared").ParentInviteDetailResponse>(
    `/schools/${schoolId}/parent-invites/${inviteId}`,
  );
}

export function deleteParentInvite(schoolId: string, inviteId: string) {
  return request<void>(`/schools/${schoolId}/parent-invites/${inviteId}`, {
    method: "DELETE",
  });
}

export async function importParentInvites(schoolId: string, file: File) {
  const formData = new FormData();
  formData.append("file", file);

  const token = await getIdToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}/schools/${schoolId}/parent-invites/import`, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.error ?? "Import failed");
  }

  return res.json() as Promise<import("@dugads/shared").ParentInviteImportResponse>;
}

// --- System Settings (076) ---

export function getSettings() {
  return request<import("@dugads/shared").SystemSettingListResponse>("/settings");
}

export function updateSetting(key: string, value: string) {
  return request<import("@dugads/shared").SystemSettingPublic>(`/settings/${encodeURIComponent(key)}`, {
    method: "PUT",
    body: JSON.stringify({ value }),
  });
}

// --- School Associations (072) ---

export function getSchoolAssociations() {
  return request<import("@dugads/shared").SchoolAssociationListResponse>("/me/school-associations");
}

export function createSchoolAssociation(data: { school_id: string; school_email?: string }) {
  return request<{ id: string; school_id: string; status: string; school_email: string | null }>("/me/school-association", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function deleteSchoolAssociation(schoolId: string) {
  return request<void>(`/me/school-association/${schoolId}`, { method: "DELETE" });
}

export function getSchoolEmails() {
  return request<import("@dugads/shared").SchoolEmailListResponse>("/me/school-emails");
}

export function addSchoolEmail(email: string) {
  return request<import("@dugads/shared").SchoolEmailAddResponse>("/me/school-emails", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export function deleteSchoolEmail(emailId: string) {
  return request<void>(`/me/school-emails/${emailId}`, { method: "DELETE" });
}

// --- User Preferences (073) ---

export function getPreferences() {
  return request<import("@dugads/shared").UserPreferencePublic>("/me/preferences");
}

export function updatePreferences(data: Partial<import("@dugads/shared").UserPreferencePublic>) {
  return request<import("@dugads/shared").UserPreferencePublic>("/me/preferences", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function completeOnboarding() {
  return request<void>("/me/onboarding-complete", { method: "POST" });
}

export function getEmailLogs(params?: { page?: number; page_size?: number; status?: string; template?: string; search?: string }) {
  const qs = new URLSearchParams();
  if (params?.page) qs.set("page", String(params.page));
  if (params?.page_size) qs.set("page_size", String(params.page_size));
  if (params?.status) qs.set("status", params.status);
  if (params?.template) qs.set("template", params.template);
  if (params?.search) qs.set("search", params.search);
  const query = qs.toString();
  return request<import("@dugads/shared").EmailLogListResponse>(`/admin/emails${query ? `?${query}` : ""}`);
}

// --- Family Sharing (077) ---

export function getFamilyGroup() {
  return request<import("@dugads/shared").FamilyGroupResponse>("/me/family-group");
}

export function createFamilyGroup() {
  return request<{ group: import("@dugads/shared").FamilyGroupPublic }>("/me/family-group", { method: "POST" });
}

export function updateFamilyGroup(name: string) {
  return request<{ group: import("@dugads/shared").FamilyGroupPublic }>("/me/family-group", {
    method: "PUT",
    body: JSON.stringify({ name }),
  });
}

export function inviteFamilyMember(email: string, label: string) {
  return request<{ group: import("@dugads/shared").FamilyGroupPublic }>("/me/family-group/members", {
    method: "POST",
    body: JSON.stringify({ email, label }),
  });
}

export function revokeFamilyMember(memberId: string) {
  return request<{ group: import("@dugads/shared").FamilyGroupPublic }>(`/me/family-group/members/${memberId}`, {
    method: "DELETE",
  });
}

export function addFamilyChild(payload: { family_code?: string; player_id?: string }) {
  return request<{ group: import("@dugads/shared").FamilyGroupPublic }>("/me/family-group/children", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function removeFamilyChild(childId: string) {
  return request<{ group: import("@dugads/shared").FamilyGroupPublic }>(`/me/family-group/children/${childId}`, {
    method: "DELETE",
  });
}

export function getFamilyInvitePreview(token: string) {
  return request<{ preview: import("@dugads/shared").FamilyAcceptPreview; email: string }>(
    `/family/invite/${token}/preview`,
  );
}

export function acceptFamilyInvite(token: string) {
  return request<{ group: import("@dugads/shared").FamilyGroupPublic }>("/me/family-group/accept", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

// ---- Rugby scores (081, renamed 098) ----

export function getRugbyMatchScore(matchId: string) {
  return request<import("@dugads/shared").RugbyScoreResponse>(`/rugby/matches/${matchId}/score`);
}

export function recordRugbyScoreEvent(
  matchId: string,
  data: {
    team_side: "home" | "away";
    event_type:
      | "try"
      | "conversion"
      | "penalty"
      | "drop_goal"
      | "yellow_card"
      | "red_card"
      | "scrum_won"
      | "scrum_lost"
      | "lineout_won"
      | "lineout_lost";
    minute?: number | null;
    player_id?: string | null;
    note?: string | null;
  },
) {
  const path = `/rugby/matches/${matchId}/score`;
  return queueableWrite(
    { sport: "rugby", op: "record", matchId, label: `${data.event_type} (${data.team_side})`, method: "POST", path, body: data },
    () =>
      request<import("@dugads/shared").RugbyScoreResponse>(path, {
        method: "POST",
        body: JSON.stringify(data),
      }),
  );
}

export function deleteRugbyScoreEvent(matchId: string, eventId: string) {
  const path = `/rugby/matches/${matchId}/score/events/${eventId}`;
  return queueableWrite(
    { sport: "rugby", op: "undo", matchId, label: "Undo event", method: "DELETE", path, body: null },
    () => request<import("@dugads/shared").RugbyScoreResponse>(path, { method: "DELETE" }),
  );
}

export function finalizeRugbyMatchScore(matchId: string) {
  const path = `/rugby/matches/${matchId}/score/finalize`;
  return queueableWrite(
    { sport: "rugby", op: "finalize", matchId, label: "Finalize", method: "POST", path, body: null },
    () => request<import("@dugads/shared").RugbyScoreResponse>(path, { method: "POST" }),
  );
}

export function reopenRugbyMatchScore(matchId: string) {
  return request<import("@dugads/shared").RugbyScoreResponse>(`/rugby/matches/${matchId}/score/reopen`, {
    method: "POST",
  });
}

export function correctRugbyMatchScore(
  matchId: string,
  data: { team_side: "home" | "away"; points: number; note: string; minute?: number | null },
) {
  const path = `/rugby/matches/${matchId}/score/correction`;
  return queueableWrite(
    { sport: "rugby", op: "correct", matchId, label: `Correction ${data.points} (${data.team_side})`, method: "PATCH", path, body: data },
    () =>
      request<import("@dugads/shared").RugbyScoreResponse>(path, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
  );
}

export function getRugbyEventResults(eventId: string) {
  return request<import("@dugads/shared").RugbyEventResultsResponse>(`/rugby/events/${eventId}/results`);
}

/**
 * 084c -- Subscribe to live score updates for an event via SSE.
 * Holds an HTTP connection open and invokes `onScore` for every
 * `event: score` frame. Reconnects on disconnect with a fresh token.
 * Returns a stop function.
 */
export interface ScoreStreamUpdate {
  event_id: string;
  match_id: string;
  home_score: number;
  away_score: number;
  final: boolean;
  /** Present only when the update is a freshly recorded score (not undo/finalize). */
  last_event_type?: "try" | "conversion" | "penalty" | "drop_goal" | "correction";
  last_team_side?: "home" | "away";
  last_points?: number;
}

export function subscribeRugbyScoreStream(
  eventId: string,
  onScore: (payload: ScoreStreamUpdate) => void,
): () => void {
  let cancelled = false;
  let controller: AbortController | null = null;

  async function connect(): Promise<void> {
    if (cancelled) return;
    const token = await getIdToken();
    if (!token) {
      // Not signed in; retry in a bit in case the user signs in.
      if (!cancelled) setTimeout(connect, 5_000);
      return;
    }
    controller = new AbortController();
    try {
      const res = await fetch(`${API_BASE}/rugby/events/${eventId}/score-stream`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        if (!cancelled) setTimeout(connect, 5_000);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (!cancelled) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Split on blank-line frame separator.
        let idx = buffer.indexOf("\n\n");
        while (idx !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          // Ignore comments (lines starting with ":") and non-score events.
          let eventName = "message";
          let dataStr = "";
          for (const line of frame.split("\n")) {
            if (line.startsWith(":")) continue;
            if (line.startsWith("event:")) eventName = line.slice(6).trim();
            else if (line.startsWith("data:")) dataStr += line.slice(5).trim();
          }
          if (eventName === "score" && dataStr) {
            try { onScore(JSON.parse(dataStr)); } catch { /* malformed frame */ }
          }
          idx = buffer.indexOf("\n\n");
        }
      }
    } catch {
      /* network error -- fall through to retry */
    }
    if (!cancelled) setTimeout(connect, 2_000);
  }

  connect();

  return () => {
    cancelled = true;
    controller?.abort();
  };
}

export function getRugbyLeagueTable(
  schoolId: string,
  params?: { age_group_id?: string; season?: number },
) {
  const q = new URLSearchParams();
  if (params?.age_group_id) q.set("age_group_id", params.age_group_id);
  if (params?.season) q.set("season", String(params.season));
  const qs = q.toString();
  return request<import("@dugads/shared").RugbyLeagueTableResponse>(
    `/schools/${schoolId}/rugby/league-table${qs ? `?${qs}` : ""}`,
  );
}

// ---- Rugby Sevens / Tens scores (160) -- dedicated module, /rugby-sevens/* ----
// Reuses the shared rugby score response types (identical point system).

export function getRugbySevensMatchScore(matchId: string) {
  return request<import("@dugads/shared").RugbyScoreResponse>(`/rugby-sevens/matches/${matchId}/score`);
}

export function recordRugbySevensScoreEvent(
  matchId: string,
  data: {
    team_side: "home" | "away";
    event_type:
      | "try"
      | "conversion"
      | "penalty"
      | "drop_goal"
      | "yellow_card"
      | "red_card"
      | "scrum_won"
      | "scrum_lost"
      | "lineout_won"
      | "lineout_lost";
    minute?: number | null;
    player_id?: string | null;
    note?: string | null;
  },
) {
  const path = `/rugby-sevens/matches/${matchId}/score`;
  return queueableWrite(
    { sport: "rugby_sevens", op: "record", matchId, label: `${data.event_type} (${data.team_side})`, method: "POST", path, body: data },
    () =>
      request<import("@dugads/shared").RugbyScoreResponse>(path, {
        method: "POST",
        body: JSON.stringify(data),
      }),
  );
}

export function deleteRugbySevensScoreEvent(matchId: string, eventId: string) {
  const path = `/rugby-sevens/matches/${matchId}/score/events/${eventId}`;
  return queueableWrite(
    { sport: "rugby_sevens", op: "undo", matchId, label: "Undo event", method: "DELETE", path, body: null },
    () => request<import("@dugads/shared").RugbyScoreResponse>(path, { method: "DELETE" }),
  );
}

export function finalizeRugbySevensMatchScore(matchId: string) {
  const path = `/rugby-sevens/matches/${matchId}/score/finalize`;
  return queueableWrite(
    { sport: "rugby_sevens", op: "finalize", matchId, label: "Finalize", method: "POST", path, body: null },
    () => request<import("@dugads/shared").RugbyScoreResponse>(path, { method: "POST" }),
  );
}

export function reopenRugbySevensMatchScore(matchId: string) {
  return request<import("@dugads/shared").RugbyScoreResponse>(`/rugby-sevens/matches/${matchId}/score/reopen`, {
    method: "POST",
  });
}

export function getRugbySevensEventResults(eventId: string) {
  return request<import("@dugads/shared").RugbyEventResultsResponse>(`/rugby-sevens/events/${eventId}/results`);
}

export function subscribeRugbySevensScoreStream(
  eventId: string,
  onScore: (payload: ScoreStreamUpdate) => void,
): () => void {
  let cancelled = false;
  let controller: AbortController | null = null;
  async function connect(): Promise<void> {
    if (cancelled) return;
    const token = await getIdToken();
    if (!token) {
      if (!cancelled) setTimeout(connect, 5_000);
      return;
    }
    controller = new AbortController();
    try {
      const res = await fetch(`${API_BASE}/rugby-sevens/events/${eventId}/score-stream`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      if (!res.ok || !res.body) {
        if (!cancelled) setTimeout(connect, 5_000);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (!cancelled) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx = buffer.indexOf("\n\n");
        while (idx !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          let eventName = "message";
          let dataStr = "";
          for (const line of frame.split("\n")) {
            if (line.startsWith(":")) continue;
            if (line.startsWith("event:")) eventName = line.slice(6).trim();
            else if (line.startsWith("data:")) dataStr += line.slice(5).trim();
          }
          if (eventName === "score" && dataStr) {
            try { onScore(JSON.parse(dataStr)); } catch { /* malformed frame */ }
          }
          idx = buffer.indexOf("\n\n");
        }
      }
    } catch {
      /* network error -- retry */
    }
    if (!cancelled) setTimeout(connect, 2_000);
  }
  connect();
  return () => {
    cancelled = true;
    controller?.abort();
  };
}

export function getNetballLeagueTable(
  schoolId: string,
  params?: { age_group_id?: string; season?: number },
) {
  const q = new URLSearchParams();
  if (params?.age_group_id) q.set("age_group_id", params.age_group_id);
  if (params?.season) q.set("season", String(params.season));
  const qs = q.toString();
  return request<import("@dugads/shared").NetballLeagueTableResponse>(
    `/schools/${schoolId}/netball/league-table${qs ? `?${qs}` : ""}`,
  );
}

// ---- Netball scores (098) ----

export function getNetballMatchScore(matchId: string) {
  return request<import("@dugads/shared").NetballScoreResponse>(`/netball/matches/${matchId}/score`);
}

export function recordNetballScoreEvent(
  matchId: string,
  data: {
    team_side: "home" | "away";
    event_type: "goal" | "missed_goal" | "intercept" | "yellow_card" | "red_card";
    quarter: number;
    minute?: number | null;
    player_id?: string | null;
    note?: string | null;
  },
) {
  const path = `/netball/matches/${matchId}/score`;
  return queueableWrite(
    { sport: "netball", op: "record", matchId, label: `${data.event_type} (${data.team_side})`, method: "POST", path, body: data },
    () =>
      request<import("@dugads/shared").NetballScoreResponse>(path, {
        method: "POST",
        body: JSON.stringify(data),
      }),
  );
}

export function deleteNetballScoreEvent(matchId: string, eventId: string) {
  const path = `/netball/matches/${matchId}/score/events/${eventId}`;
  return queueableWrite(
    { sport: "netball", op: "undo", matchId, label: "Undo event", method: "DELETE", path, body: null },
    () => request<import("@dugads/shared").NetballScoreResponse>(path, { method: "DELETE" }),
  );
}

export function finalizeNetballMatchScore(matchId: string) {
  const path = `/netball/matches/${matchId}/score/finalize`;
  return queueableWrite(
    { sport: "netball", op: "finalize", matchId, label: "Finalize", method: "POST", path, body: null },
    () => request<import("@dugads/shared").NetballScoreResponse>(path, { method: "POST" }),
  );
}

export function reopenNetballMatchScore(matchId: string) {
  return request<import("@dugads/shared").NetballScoreResponse>(
    `/netball/matches/${matchId}/score/reopen`,
    { method: "POST" },
  );
}

export function correctNetballMatchScore(
  matchId: string,
  data: {
    team_side: "home" | "away";
    quarter: number;
    points: number;
    note: string;
    minute?: number | null;
  },
) {
  const path = `/netball/matches/${matchId}/score/correction`;
  return queueableWrite(
    { sport: "netball", op: "correct", matchId, label: `Correction ${data.points} (${data.team_side})`, method: "PATCH", path, body: data },
    () =>
      request<import("@dugads/shared").NetballScoreResponse>(path, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
  );
}

// ---- Event Detail Tabs (082) ----

export function getEventDetailBlocks(eventId: string) {
  return request<import("@dugads/shared").EventDetailBlockListResponse>(`/events/${eventId}/detail-blocks`);
}

export function createEventDetailBlock(
  eventId: string,
  data: import("@dugads/shared").CreateBlockInput,
) {
  return request<{ block: import("@dugads/shared").EventDetailBlockPublic }>(`/events/${eventId}/detail-blocks`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateEventDetailBlock(
  eventId: string,
  blockId: string,
  data: import("@dugads/shared").UpdateBlockInput,
) {
  return request<{ block: import("@dugads/shared").EventDetailBlockPublic }>(`/events/${eventId}/detail-blocks/${blockId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteEventDetailBlock(eventId: string, blockId: string) {
  return request<{ success: true }>(`/events/${eventId}/detail-blocks/${blockId}`, { method: "DELETE" });
}

export function reorderEventDetailBlocks(eventId: string, order: string[]) {
  return request<{ success: true }>(`/events/${eventId}/detail-blocks/reorder`, {
    method: "POST",
    body: JSON.stringify({ order }),
  });
}

export function getEventPolls(eventId: string) {
  return request<import("@dugads/shared").EventPollListResponse>(`/events/${eventId}/polls`);
}

export function createEventPoll(eventId: string, data: import("@dugads/shared").CreatePollInput) {
  return request<{ poll: import("@dugads/shared").EventPollPublic }>(`/events/${eventId}/polls`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function respondToPoll(pollId: string, data: import("@dugads/shared").RespondPollInput) {
  return request<{ poll: import("@dugads/shared").EventPollPublic }>(`/polls/${pollId}/responses`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function withdrawPollResponse(pollId: string, option?: string) {
  const qs = option ? `?option=${encodeURIComponent(option)}` : "";
  return request<{ success: true }>(`/polls/${pollId}/responses/me${qs}`, { method: "DELETE" });
}

export function closeEventPoll(pollId: string) {
  return request<{ poll: import("@dugads/shared").EventPollPublic }>(`/polls/${pollId}/close`, { method: "POST" });
}

// ---- Event Series (083) ----

export function getEventSeriesList(schoolId: string) {
  return request<import("@dugads/shared").EventSeriesListResponse>(`/schools/${schoolId}/event-series`);
}

export function createEventSeries(schoolId: string, data: import("@dugads/shared").CreateEventSeriesInput) {
  return request<import("@dugads/shared").EventSeriesResponse>(`/schools/${schoolId}/event-series`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function getEventSeries(id: string) {
  return request<import("@dugads/shared").EventSeriesResponse>(`/event-series/${id}`);
}

export function deleteEventSeries(id: string) {
  return request<{ success: true }>(`/event-series/${id}`, { method: "DELETE" });
}

export function skipSeriesOccurrence(id: string, date: string, reason?: string) {
  return request<{ success: true; affected: number }>(`/event-series/${id}/exceptions`, {
    method: "POST",
    body: JSON.stringify({ date, reason }),
  });
}

// ---- Policies & Consents (085) ----

export function getCurrentPolicy(type: import("@dugads/shared").AnyPolicyType) {
  return request<{ policy: import("@dugads/shared").PolicyVersionPublic }>(`/policies/${type}/current`);
}

// super_admin -- publish a new version of a policy (auto-sunsets the prior one).
export function publishPolicy(
  type: import("@dugads/shared").AnyPolicyType,
  data: import("@dugads/shared").UpsertPolicyInput,
) {
  return request<{ policy: import("@dugads/shared").PolicyVersionPublic }>(`/admin/policies/${type}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function getPolicyVersion(type: import("@dugads/shared").PolicyType, version: string) {
  return request<{ policy: import("@dugads/shared").PolicyVersionPublic }>(`/policies/${type}/${version}`);
}

export function getMyConsents() {
  return request<import("@dugads/shared").UserConsentsResponse>(`/me/consents`);
}

export function recordConsent(data: import("@dugads/shared").RecordConsentInput) {
  return request<{ consent: import("@dugads/shared").UserConsentPublic }>(`/me/consents`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ---- Coach Notes (085) ----

export function getCoachNotes(playerId: string) {
  return request<{ notes: import("@dugads/shared").PlayerCoachNotePublic[] }>(`/players/${playerId}/coach-notes`);
}

export function createCoachNote(playerId: string, data: import("@dugads/shared").CreateCoachNoteInput) {
  return request<{ note: import("@dugads/shared").PlayerCoachNotePublic }>(`/players/${playerId}/coach-notes`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateCoachNote(playerId: string, noteId: string, data: import("@dugads/shared").UpdateCoachNoteInput) {
  return request<{ note: import("@dugads/shared").PlayerCoachNotePublic }>(`/players/${playerId}/coach-notes/${noteId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteCoachNote(playerId: string, noteId: string) {
  return request<{ success: true }>(`/players/${playerId}/coach-notes/${noteId}`, { method: "DELETE" });
}

// ---- Sponsor Offers (086) ----

export function getSponsorOffers(sponsorId: string) {
  return request<import("@dugads/shared").SponsorOfferListResponse>(`/sponsors/${sponsorId}/offers`);
}

export function createSponsorOffer(sponsorId: string, data: import("@dugads/shared").CreateOfferInput) {
  return request<{ offer: import("@dugads/shared").SponsorOfferPublic }>(`/sponsors/${sponsorId}/offers`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateSponsorOffer(offerId: string, data: import("@dugads/shared").UpdateOfferInput) {
  return request<{ offer: import("@dugads/shared").SponsorOfferPublic }>(`/offers/${offerId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export function deleteSponsorOffer(offerId: string) {
  return request<{ success: true }>(`/offers/${offerId}`, { method: "DELETE" });
}

export function getPublicOffers() {
  return request<{ offers: import("@dugads/shared").SponsorOfferPublic[]; _actions: { can_create: boolean } }>(`/offers/public`);
}

export function getSchoolOffers(schoolId: string) {
  return request<{ school_id: string; offers: import("@dugads/shared").SponsorOfferPublic[]; _actions: { can_claim: boolean } }>(`/schools/${schoolId}/offers`);
}

export function claimOffer(offerId: string, data: import("@dugads/shared").ClaimOfferInput) {
  return request<{ claim: import("@dugads/shared").SponsorOfferClaimPublic }>(`/offers/${offerId}/claim`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function getSponsorClaims(sponsorId: string) {
  return request<{ claims: import("@dugads/shared").SponsorOfferClaimPublic[] }>(`/sponsors/${sponsorId}/claims`);
}

export function updateClaimStatus(claimId: string, status: import("@dugads/shared").OfferClaimStatus) {
  return request<{ claim: import("@dugads/shared").SponsorOfferClaimPublic }>(`/claims/${claimId}`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

// ==== Shop (090) ====
import type {
  ProviderPublic,
  ProductPublic,
  ProviderListResponse,
  ProviderDetailResponse,
  CartResponse,
  OrderListResponse,
  OrderResponse,
  SubmitOrderInput,
  QuoteFormResponse,
  QuoteSubmitResponse,
  AdminOrderListResponse,
  AdminOrderFilters,
  UpdateOrderStatusInput,
} from "@dugads/shared";

export function getShopProviders() {
  return request<ProviderListResponse>("/shop/providers");
}

export function getShopProvider(slug: string) {
  return request<ProviderDetailResponse>(`/shop/providers/${slug}`);
}

export function getShopCart(providerId: string) {
  return request<CartResponse>(`/shop/cart?provider_id=${encodeURIComponent(providerId)}`);
}

export function upsertCartItem(data: { provider_id: string; product_id: string; quantity: number }) {
  return request<CartResponse>("/shop/cart/items", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function removeCartItem(id: string) {
  return request<CartResponse>(`/shop/cart/items/${id}`, { method: "DELETE" });
}

export function submitShopOrder(data: SubmitOrderInput) {
  return request<OrderResponse>("/shop/orders", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function getMyOrders() {
  return request<OrderListResponse>("/me/orders");
}

export function getOrder(slug: string) {
  return request<OrderResponse>(`/orders/${slug}`);
}

// 092 -- external_redirect + admin shop CRUD.

export function resolveProviderRedirect(
  slug: string,
  body: { school_id?: string; platform?: string; app_version?: string } = {},
) {
  return request<{ provider_id: string; provider_slug: string; redirect_url: string }>(
    `/shop/providers/${slug}/redirect`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export function getAdminProviders() {
  return request<{ providers: ProviderPublic[] }>("/admin/providers");
}

export function createAdminProvider(data: Record<string, unknown>) {
  return request<{ provider: ProviderPublic }>("/admin/providers", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateAdminProvider(id: string, data: Record<string, unknown>) {
  return request<{ provider: ProviderPublic }>(`/admin/providers/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export function deleteAdminProvider(id: string) {
  return request<{ provider: ProviderPublic }>(`/admin/providers/${id}`, { method: "DELETE" });
}

export async function uploadAdminProviderLogo(id: string, file: File): Promise<{ provider: ProviderPublic }> {
  const token = await getIdToken();
  const fd = new FormData();
  fd.append("logo", file);
  const res = await fetch(`${API_BASE}/admin/providers/${id}/logo`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    throw new ApiError(res.status, body.error || `HTTP ${res.status}`, body);
  }
  return res.json();
}

export function createAdminProduct(providerId: string, data: Record<string, unknown>) {
  return request<{ product: ProductPublic }>(`/admin/providers/${providerId}/products`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function updateAdminProduct(id: string, data: Record<string, unknown>) {
  return request<{ product: ProductPublic }>(`/admin/products/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export function deleteAdminProduct(id: string) {
  return request<void>(`/admin/products/${id}`, { method: "DELETE" });
}

// 093 -- Quote flow.

export function getQuoteForm(slug: string) {
  return request<QuoteFormResponse>(`/shop/providers/${slug}/quote-form`);
}

export function submitQuote(
  slug: string,
  payload: { form: Record<string, string>; school_id?: string | null },
) {
  return request<QuoteSubmitResponse>(`/shop/providers/${slug}/quote`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// 094 -- Admin orders dashboard + status PATCH.

export function getAdminOrders(filters: AdminOrderFilters & { page?: number; page_size?: number } = {}) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
  }
  const qs = params.toString();
  return request<AdminOrderListResponse>(`/admin/orders${qs ? `?${qs}` : ""}`);
}

export function getAdminOrder(slug: string) {
  return request<OrderResponse>(`/admin/orders/${slug}`);
}

export function patchOrderStatus(slug: string, body: UpdateOrderStatusInput) {
  return request<OrderResponse>(`/orders/${slug}/status`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

// 095 -- Provider secrets CRUD + order dispatch retry.

export function getProviderSecrets(providerId: string, revealName?: string) {
  const qs = revealName ? `?reveal=${encodeURIComponent(revealName)}` : "";
  return request<{ secrets: Array<{ name: string; rotated_at: string; value?: string }> }>(
    `/admin/providers/${providerId}/secrets${qs}`,
  );
}

export function upsertProviderSecret(providerId: string, name: string, value: string) {
  return request<{ secrets: Array<{ name: string; rotated_at: string }> }>(
    `/admin/providers/${providerId}/secrets`,
    { method: "PATCH", body: JSON.stringify({ name, value }) },
  );
}

export function retryOrderDispatch(slug: string) {
  return request<OrderResponse>(`/admin/orders/${slug}/retry-dispatch`, {
    method: "POST",
  });
}

// 096 -- Cashback.

import type {
  CashbackStatementResponse,
  SchoolCashbackResponse,
  AdminCashbackLedgerResponse,
  CashbackSplitPublic,
  CashbackBatchIngestInput,
  CashbackBatchIngestResponse,
  LinkAccountStartResponse,
  LinkAccountCallbackResponse,
  CashbackAccrualPublic,
} from "@dugads/shared";

export function startProviderLink(slug: string) {
  return request<LinkAccountStartResponse>(`/shop/providers/${slug}/link`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function completeProviderLink(slug: string, state: string, external_account_ref: string) {
  return request<LinkAccountCallbackResponse>(`/shop/providers/${slug}/link/callback`, {
    method: "POST",
    body: JSON.stringify({ state, external_account_ref }),
  });
}

export function unlinkProvider(linkId: string) {
  return request<void>(`/me/provider-links/${linkId}`, { method: "DELETE" });
}

export function getMyCashback() {
  return request<CashbackStatementResponse>("/me/cashback");
}

export function getSchoolCashback(schoolId: string) {
  return request<SchoolCashbackResponse>(`/schools/${schoolId}/cashback`);
}

export function getAdminCashbackLedger(filters: Record<string, string | number | undefined> = {}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== "") qs.set(k, String(v));
  }
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request<AdminCashbackLedgerResponse>(`/admin/cashback/ledger${suffix}`);
}

export function getCashbackSplit(providerId: string) {
  return request<{ split: CashbackSplitPublic }>(`/admin/providers/${providerId}/cashback-split`);
}

export function updateCashbackSplit(
  providerId: string,
  data: { parent_pct: number; school_pct: number; dugads_pct: number },
) {
  return request<{ split: CashbackSplitPublic }>(`/admin/providers/${providerId}/cashback-split`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export function ingestCashbackBatch(providerId: string, body: CashbackBatchIngestInput) {
  return request<CashbackBatchIngestResponse>(`/admin/providers/${providerId}/cashback/ingest`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function reverseCashbackAccrual(accrualId: string, reason: string) {
  return request<{ accrual: CashbackAccrualPublic }>(`/admin/cashback/accruals/${accrualId}/reverse`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

// 097 -- Volunteer Coordination.

import type {
  EventTaskListResponse,
  EventTaskResponse,
  EventTaskAssignmentPublic,
  VolunteerOfferListResponse,
  VolunteerOfferResponse,
  CreateEventTaskInput,
  UpdateEventTaskInput,
  ClaimTaskInput,
  UpdateAssignmentStatusInput,
  CreateVolunteerOfferInput,
  UpdateVolunteerOfferInput,
} from "@dugads/shared";

export function getEventTasks(eventId: string) {
  return request<EventTaskListResponse>(`/events/${eventId}/tasks`);
}

export function createEventTask(eventId: string, body: CreateEventTaskInput) {
  return request<EventTaskResponse>(`/events/${eventId}/tasks`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateEventTask(taskId: string, body: UpdateEventTaskInput) {
  return request<EventTaskResponse>(`/tasks/${taskId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function cancelEventTask(taskId: string) {
  return request<void>(`/tasks/${taskId}`, { method: "DELETE" });
}

export function claimTask(taskId: string, body: ClaimTaskInput) {
  return request<{ assignment: EventTaskAssignmentPublic }>(`/tasks/${taskId}/claim`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function unclaimTask(assignmentId: string) {
  return request<{ assignment: EventTaskAssignmentPublic }>(`/assignments/${assignmentId}`, {
    method: "DELETE",
  });
}

export function updateAssignmentStatus(assignmentId: string, body: UpdateAssignmentStatusInput) {
  return request<{ assignment: EventTaskAssignmentPublic }>(`/assignments/${assignmentId}/status`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function getMyVolunteerOffers() {
  return request<VolunteerOfferListResponse>(`/me/volunteer-offers`);
}

export function createMyVolunteerOffer(body: CreateVolunteerOfferInput) {
  return request<VolunteerOfferResponse>(`/me/volunteer-offers`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateMyVolunteerOffer(id: string, body: UpdateVolunteerOfferInput) {
  return request<VolunteerOfferResponse>(`/me/volunteer-offers/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function deleteMyVolunteerOffer(id: string) {
  return request<void>(`/me/volunteer-offers/${id}`, { method: "DELETE" });
}

export function getSchoolVolunteerOffers(schoolId: string, filters: { category?: string; search?: string } = {}) {
  const qs = new URLSearchParams();
  if (filters.category) qs.set("category", filters.category);
  if (filters.search) qs.set("search", filters.search);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return request<VolunteerOfferListResponse>(`/schools/${schoolId}/volunteer-offers${suffix}`);
}

// ============================================================
// Venues (100) -- standalone Venue CRUD + per-event EventVenue CRUD
// ============================================================

import type {
  VenueListResponse,
  VenuePublic,
  EventVenueListResponse,
  EventVenuePublic,
  CreateVenueInput,
  UpdateVenueInput,
  CreateEventVenueInput,
  UpdateEventVenueInput,
} from "@dugads/shared";

export function listVenues() {
  return request<VenueListResponse>("/venues");
}

export function createVenue(body: CreateVenueInput) {
  return request<{ venue: VenuePublic }>("/venues", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function updateVenue(id: string, body: UpdateVenueInput) {
  return request<{ venue: VenuePublic }>(`/venues/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function deleteVenue(id: string) {
  return request<void>(`/venues/${id}`, { method: "DELETE" });
}

export function listEventVenues(eventId: string) {
  return request<EventVenueListResponse>(`/events/${eventId}/venues`);
}

export function addEventVenue(eventId: string, body: CreateEventVenueInput) {
  return request<{ event_venue: EventVenuePublic }>(`/events/${eventId}/venues`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function patchEventVenue(
  eventId: string,
  eventVenueId: string,
  body: UpdateEventVenueInput,
) {
  return request<{ event_venue: EventVenuePublic }>(
    `/events/${eventId}/venues/${eventVenueId}`,
    { method: "PATCH", body: JSON.stringify(body) },
  );
}

export function deleteEventVenue(eventId: string, eventVenueId: string) {
  return request<void>(`/events/${eventId}/venues/${eventVenueId}`, { method: "DELETE" });
}

// ─── Google Places proxy ──────────────────────────────────────────────────────

export interface PlaceSuggestion {
  placeId: string;
  text: string;
  mainText: string | null;
  secondaryText: string | null;
}

export interface PlaceDetails {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
}

export function getPlacesAutocomplete(input: string, sessiontoken: string) {
  const params = new URLSearchParams({ input, sessiontoken });
  return request<{ suggestions: PlaceSuggestion[] }>(`/places/autocomplete?${params}`);
}

export function getPlaceDetails(placeId: string, sessiontoken: string) {
  const params = new URLSearchParams({ placeId, sessiontoken });
  return request<PlaceDetails>(`/places/details?${params}`);
}

// ---- POPIA / GDPR data rights (121) ----

export function requestDataExport() {
  return request<import("@dugads/shared").DataExportResponse>("/me/data-export", {
    method: "POST",
  });
}

export function getDataExportStatus() {
  return request<{ request: import("@dugads/shared").DataExportRequestPublic | null }>(
    "/me/data-export",
  );
}

/** Fetch the export bundle with auth and trigger a browser download. */
export async function downloadDataExport(id: string): Promise<void> {
  const token = await getIdToken();
  const res = await fetch(`${API_BASE}/me/data-export/${id}/download`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new ApiError(res.status, "Download failed");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "dugads-data-export.json";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function getAccountDeletionStatus() {
  return request<import("@dugads/shared").AccountDeletionResponse>("/me/account-deletion");
}

export function requestAccountDeletion() {
  return request<import("@dugads/shared").AccountDeletionResponse>("/me/account-deletion", {
    method: "POST",
  });
}

export function cancelAccountDeletion() {
  return request<import("@dugads/shared").AccountDeletionResponse>(
    "/me/account-deletion/cancel",
    { method: "POST" },
  );
}

// ---- Schools-as-Venues (131) ----

export function addVenue(data: import("@dugads/shared").AddVenueInput) {
  return request<import("@dugads/shared").SchoolResponse>("/schools/with-verification", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function getPendingVenues() {
  return request<import("@dugads/shared").PendingVenuesResponse>("/admin/pending-venues");
}

export function verifyVenue(id: string) {
  return request<import("@dugads/shared").SchoolResponse>(
    `/admin/pending-venues/${id}/verify`,
    { method: "POST" },
  );
}

export function rejectVenue(id: string) {
  return request<void>(`/admin/pending-venues/${id}`, { method: "DELETE" });
}

// ---- Spectator self-registration (132) ----

export function getRegistrationPolicy() {
  return request<import("@dugads/shared").RegistrationPolicy>("/auth/registration-policy");
}

export function linkToSchool(data: import("@dugads/shared").LinkToSchoolInput) {
  return request<import("@dugads/shared").LinkToSchoolResponse>("/me/link-to-school", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export function confirmParentLink(token: string) {
  return request<{ status: string }>("/parent-link/confirm", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

// 164 -- school-seeded registration links
export function getRegistrationLinkPreview(token: string) {
  return request<import("@dugads/shared").RegistrationLinkPreviewResponse>(
    `/registration-link/${encodeURIComponent(token)}`,
  );
}

export function claimRegistrationLink(token: string) {
  return request<{ status: string }>(`/registration-link/${encodeURIComponent(token)}/claim`, {
    method: "POST",
  });
}

export function issueRegistrationLinks(schoolId: string, inviteId?: string) {
  return request<import("@dugads/shared").IssueRegistrationLinksResponse>(
    `/schools/${schoolId}/parent-invites/issue-links`,
    { method: "POST", body: JSON.stringify(inviteId ? { invite_id: inviteId } : {}) },
  );
}

// 165 -- yearly active-parent reconciliation
export function reconcileActiveParents(schoolId: string, activeEmails: string[]) {
  return request<import("@dugads/shared").ReconciliationResponse>(
    `/schools/${schoolId}/parent-reconciliation`,
    { method: "POST", body: JSON.stringify({ active_emails: activeEmails }) },
  );
}

// --- Coach-suggestions batch UI (144-151) ---

// 144 -- per-school settings
export function getSchoolSettings(schoolId: string) {
  return request<import("@dugads/shared").SchoolSettingsResponse>(`/schools/${schoolId}/settings`);
}
export function updateSchoolSetting(schoolId: string, key: string, value: string) {
  return request<import("@dugads/shared").SchoolSettingPublic>(`/schools/${schoolId}/settings/${key}`, {
    method: "PUT",
    body: JSON.stringify({ value }),
  });
}

// 145 -- player health & emergency record
export function getPlayerHealth(playerId: string) {
  return request<import("@dugads/shared").PlayerHealthResponse>(`/players/${playerId}/health`);
}
export function upsertPlayerHealth(playerId: string, data: import("@dugads/shared").UpsertPlayerHealthInput) {
  return request<import("@dugads/shared").PlayerHealthResponse>(`/players/${playerId}/health`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// 146 -- player injuries
export function getPlayerInjuries(playerId: string) {
  return request<import("@dugads/shared").PlayerInjuriesResponse>(`/players/${playerId}/injuries`);
}
export function createPlayerInjury(playerId: string, data: import("@dugads/shared").CreatePlayerInjuryInput) {
  return request<{ injury: import("@dugads/shared").PlayerInjuryPublic }>(`/players/${playerId}/injuries`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}
export function clearPlayerInjury(playerId: string, injuryId: string, data: import("@dugads/shared").ClearPlayerInjuryInput) {
  return request<{ injury: import("@dugads/shared").PlayerInjuryPublic }>(`/players/${playerId}/injuries/${injuryId}/clear`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

// 148 -- coach BokSmart number
export function updateCoach(id: string, data: { boksmart_number?: string | null }) {
  return request<import("@dugads/shared").CoachResponse>(`/coaches/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// 150 -- unassigned-player roster pool
export function getUnassignedPlayers(schoolId: string, season?: string) {
  const qs = season ? `?season=${encodeURIComponent(season)}` : "";
  return request<{ season: string; players: import("@dugads/shared").PlayerPublic[] }>(
    `/schools/${schoolId}/players/unassigned${qs}`,
  );
}

// 151 -- full registration intake
export function registrationIntake(schoolId: string, data: import("@dugads/shared").RegistrationIntakeInput) {
  return request<{
    player_id: string;
    family_code: string;
    health_captured: boolean;
    enrollment_created: boolean;
    guardian_invites: { email: string; family_code: string }[];
  }>(`/schools/${schoolId}/registration-intake`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}
